<template>
  <v-container fluid class="dtc-container">
    <!-- Zugriffshinweis -->
    <v-alert v-if="!hasDtcRole && !hasOfficeRole && !hasVersandRole" type="warning" class="mb-4">
      Sie benötigen die Rolle <strong>DTC</strong> (oder Office/Versand), um diese Seite zu nutzen.
    </v-alert>

    <v-card class="mb-4">
      <v-card-title class="d-flex align-center flex-wrap">
        <v-icon color="teal" class="mr-2">mdi-package-variant-closed</v-icon>
        DTC Retouren-Erfassung
        <v-spacer></v-spacer>
        <v-chip class="ma-1" color="teal" size="small">A: {{ statistik.paletten_a || 0 }}</v-chip>
        <v-chip class="ma-1" color="blue" size="small">B: {{ statistik.paletten_b || 0 }}</v-chip>
        <v-chip class="ma-1" color="orange" size="small">IRMAF: {{ statistik.irmaf || 0 }}</v-chip>
        <v-chip class="ma-1" color="grey" size="small">Heute: {{ statistik.retouren_heute || 0 }}</v-chip>
      </v-card-title>

      <v-tabs v-model="tab" bg-color="grey-darken-4" show-arrows>
        <v-tab value="erfassung"><v-icon start>mdi-barcode-scan</v-icon>Erfassung</v-tab>
        <v-tab value="paletten"><v-icon start>mdi-pallet</v-icon>Paletten</v-tab>
        <v-tab value="irmaf"><v-icon start>mdi-alert-circle</v-icon>IRMAF</v-tab>
        <v-tab value="import"><v-icon start>mdi-upload</v-icon>SalesHistory</v-tab>
        <v-tab value="master"><v-icon start>mdi-database-import</v-icon>Masterliste</v-tab>
        <v-tab value="artikelkatalog"><v-icon start>mdi-table-search</v-icon>SalesHistory</v-tab>
        <v-tab value="tagesuebersicht"><v-icon start>mdi-calendar-text</v-icon>Tagesübersicht</v-tab>
        <v-tab value="output"><v-icon start>mdi-file-download</v-icon>Output</v-tab>
        <v-tab v-if="isAdmin" value="korrektur"><v-icon start>mdi-shield-edit</v-icon>Korrektur</v-tab>
        <v-tab v-if="isAdmin" value="sperren"><v-icon start>mdi-lock-alert</v-icon>Sperren</v-tab>
      </v-tabs>
    </v-card>

    <v-window v-model="tab">
      <!-- ============================ ERFASSUNG ============================ -->
      <v-window-item value="erfassung">
        <v-row>
          <!-- Linke Spalte: offene Paletten (Übersicht + manuelles Abschließen) -->
          <v-col cols="12" md="4">
            <v-card>
              <v-card-title class="text-subtitle-1 d-flex align-center">
                <v-icon start>mdi-pallet</v-icon> Offene Paletten
                <v-spacer></v-spacer>
                <v-btn
                  size="x-small" color="teal" variant="flat" class="mr-1"
                  title="Neue A-Palette anlegen"
                  @click="createPalette('A')"
                >
                  <v-icon start size="small">mdi-plus</v-icon>A
                </v-btn>
                <v-btn
                  size="x-small" color="blue" variant="flat"
                  title="Neue B-Palette anlegen"
                  @click="createPalette('B')"
                >
                  <v-icon start size="small">mdi-plus</v-icon>B
                </v-btn>
              </v-card-title>
              <v-divider></v-divider>
              <v-list density="compact">
                <v-list-item
                  v-for="p in offenePaletten"
                  :key="p.id"
                  :active="activePalette && activePalette.id === p.id"
                  @click="selectActivePalette(p)"
                >
                  <template #prepend>
                    <v-avatar :color="p.paletten_typ === 'A' ? 'teal' : 'blue'" size="32">
                      {{ p.paletten_typ }}
                    </v-avatar>
                  </template>
                  <v-list-item-title>{{ p.paletten_nr }}</v-list-item-title>
                  <v-list-item-subtitle>{{ p.anzahl_positionen }} Pos. · {{ p.gesamtgewicht }} kg</v-list-item-subtitle>
                  <template #append>
                    <v-btn
                      icon size="x-small" variant="text" color="success"
                      title="Palette an Office übergeben"
                      @click.stop="finishPalette(p)"
                    >
                      <v-icon>mdi-arrow-right-bold-box</v-icon>
                    </v-btn>
                  </template>
                </v-list-item>
                <v-list-item v-if="offenePaletten.length === 0">
                  <v-list-item-subtitle>Keine offenen Paletten – wird bei der Erfassung automatisch angelegt.</v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </v-card>

            <!-- Positionen der gewählten Palette wurden nach unten verschoben -->
          </v-col>

          <!-- Rechte Spalte: Erfassungs-Wizard (immer sichtbar, Weborder zuerst) -->
          <v-col cols="12" md="8">
            <v-card>
              <v-card-title class="d-flex align-center">
                <v-icon start color="teal">mdi-barcode-scan</v-icon> Retoure erfassen
                <v-spacer></v-spacer>
                <v-chip v-if="selectedItems.length" color="teal" size="small">{{ selectedItems.length }} Paket(e)</v-chip>
              </v-card-title>
              <v-divider></v-divider>

              <v-card-text>
                <!-- Schritt 1: Weborder -->
                <div class="step-box">
                  <div class="step-title">1. Weborder-ID scannen</div>
                  <v-text-field
                    ref="weborderField"
                    v-model="form.weborderRaw"
                    label="Weborder-ID (z.B. (Y)TFML45865896)"
                    hint="Das vorangestellte (Y) wird automatisch entfernt"
                    persistent-hint
                    density="compact"
                    @keyup.enter="searchWeborder"
                    append-inner-icon="mdi-magnify"
                    @click:append-inner="searchWeborder"
                  ></v-text-field>
                  <div v-if="form.weborderClean" class="text-caption mt-1">
                    Bereinigt: <strong>{{ form.weborderClean }}</strong>
                  </div>
                </div>

                <!-- Sales-Treffer -->
                <div v-if="salesRows.length > 0" class="step-box">
                  <div class="step-title">2. Pakete auswählen (Mehrfachauswahl)</div>
                  <v-alert v-if="irmafHinweis" type="warning" density="compact" class="mb-2">
                    Achtung: Diese Bestellung fällt unter die Datumsregel ({{ irmafHinweis }}).
                    Bitte nicht auf Palette buchen – stattdessen in die IRMAF-Liste eintragen.
                    <v-btn size="x-small" class="ml-2" color="white" variant="outlined" @click="addToIrmaf">In IRMAF-Liste</v-btn>
                  </v-alert>
                  <v-alert v-if="rueckKundeHinweis" type="info" density="compact" class="mb-2">
                    Achtung: {{ rueckKundeHinweis }} – direkt zurück an den Kunden.
                    Aufnahme nur als Notiz (kein Output, keine Palette). Sepas-Auftragsnummer ist Pflicht.
                    <v-btn size="x-small" class="ml-2" color="white" variant="outlined" @click="addToRueckKunde">Rückversand Kunde erfassen</v-btn>
                  </v-alert>

                  <div
                    v-for="(r, idx) in salesRows"
                    :key="idx"
                    class="sales-row"
                    :class="{ 'freight-row': rowState[idx] && rowState[idx].freight }"
                  >
                    <div class="d-flex align-start">
                      <v-checkbox
                        v-if="rowState[idx] && !rowState[idx].freight"
                        v-model="rowState[idx].checked"
                        density="compact"
                        hide-details
                        class="mt-0"
                        @update:model-value="onRowCheck(idx)"
                      ></v-checkbox>
                      <v-icon v-else color="grey" class="mt-2 mr-3" title="Frachtkosten – kein Paket">mdi-truck-outline</v-icon>

                      <div class="flex-grow-1">
                        <div>
                          <strong>{{ r.sku }}</strong>
                          <v-chip v-if="rowState[idx] && rowState[idx].freight" size="x-small" color="grey" class="ml-1">Fracht</v-chip>
                          <span v-if="r.product_desc"> – {{ r.product_desc }}</span>
                        </div>
                        <div class="text-caption text-medium-emphasis">
                          Zeile {{ r.order_line }} · {{ r.order_type_norm || r.order_type }}
                          <span v-if="r.order_date"> · {{ r.order_date }}</span>
                          <v-chip v-if="isRueckKundeRow(r)" color="purple" size="x-small" class="ml-1">Rück Kunde</v-chip>
                          <v-chip v-else-if="isIrmafDateRow(r)" color="orange" size="x-small" class="ml-1">IRMAF</v-chip>
                          <span v-if="r.return_reason_raw"> · Grund: {{ r.return_reason_raw }}</span>
                        </div>

                        <!-- Detailfelder pro ausgewähltem Paket -->
                        <div
                          v-if="rowState[idx] && rowState[idx].checked && !rowState[idx].freight"
                          class="package-detail mt-2"
                        >
                          <v-row dense>
                            <v-col cols="12" sm="6">
                              <v-select
                                v-if="rowState[idx].candidates.length > 1"
                                v-model="rowState[idx].cmmf"
                                :items="rowState[idx].candidates"
                                item-title="label"
                                item-value="cmmf"
                                label="CMMF wählen"
                                density="compact"
                                hide-details
                              ></v-select>
                              <v-text-field
                                v-else
                                v-model="rowState[idx].cmmf"
                                label="CMMF"
                                density="compact"
                                hide-details
                              ></v-text-field>
                            </v-col>
                            <v-col cols="6" sm="3">
                              <v-text-field
                                v-model.number="rowState[idx].menge"
                                type="number"
                                min="1"
                                label="Menge"
                                density="compact"
                                hide-details
                              ></v-text-field>
                            </v-col>
                            <v-col cols="6" sm="3">
                              <v-text-field
                                v-model="rowState[idx].tracking"
                                label="Trackingnummer"
                                density="compact"
                                hide-details
                              ></v-text-field>
                            </v-col>
                          </v-row>

                          <!-- Rücksendegrund pro Paket -->
                          <v-row dense class="mt-1">
                            <v-col cols="12">
                              <v-select
                                v-model="rowState[idx].reason"
                                :items="activeReasonItems"
                                item-title="label"
                                item-value="code"
                                label="Rücksendegrund"
                                density="compact"
                                hide-details
                                @update:model-value="rowOnReason(idx)"
                              ></v-select>
                              <div v-if="rowState[idx].reasonAuto && rowState[idx].reason" class="text-caption text-success">
                                <v-icon size="x-small">mdi-auto-fix</v-icon>
                                Automatisch erkannt – bei Bedarf ändern.
                              </div>
                            </v-col>
                          </v-row>

                          <!-- Entscheidungsbaum pro Paket -->
                          <div v-if="rowNode(idx)" class="decision-box pa-2 mt-1">
                            <div class="decision-question">{{ rowNode(idx).question }}</div>
                            <div class="d-flex ga-2 mt-2">
                              <v-btn size="small" color="success" @click="rowAnswer(idx, true)">Ja</v-btn>
                              <v-btn size="small" color="error" @click="rowAnswer(idx, false)">Nein</v-btn>
                            </div>
                          </div>

                          <!-- Manuelle Klassifizierung pro Paket -->
                          <div v-if="rowState[idx].manualChoice" class="decision-box pa-2 mt-1">
                            <div class="text-caption mb-1">Klassifizierung wählen:</div>
                            <div class="d-flex ga-2 flex-wrap">
                              <v-btn size="small" color="teal" @click="rowSetResult(idx, 'A')">A-Ware</v-btn>
                              <v-btn size="small" color="blue" @click="rowSetResult(idx, 'B')">B-Ware</v-btn>
                              <v-btn size="small" color="orange" @click="rowSetResult(idx, 'IRMAF')">IRMAF</v-btn>
                            </div>
                          </div>

                          <!-- Ergebnis pro Paket -->
                          <div v-if="rowState[idx].result" class="mt-1">
                            <v-chip :color="resultColorOf(rowState[idx].result)" size="small">
                              <v-icon start size="small">mdi-check-decagram</v-icon>
                              {{ resultLabelOf(rowState[idx].result) }}
                            </v-chip>
                            <v-btn variant="text" size="x-small" class="ml-1" @click="rowReclassify(idx)">neu bewerten</v-btn>
                            <v-btn variant="text" size="x-small" @click="rowManual(idx)">manuell wählen</v-btn>
                          </div>
                        </div>
                      </div>
                    </div>
                    <v-divider class="my-1"></v-divider>
                  </div>

                  <div class="text-caption text-medium-emphasis">
                    {{ selectedItems.length }} Paket(e) ausgewählt.
                    FREIGHT-Zeilen sind Frachtkosten und werden nicht gebucht.
                  </div>
                </div>

                <v-alert v-else-if="searched && salesRows.length === 0" type="info" density="compact" class="mb-2">
                  Keine Bestellung in der SalesHistory gefunden. Du kannst die Daten manuell eingeben.
                  <div class="mt-2">
                    <v-btn size="small" color="orange-darken-2" variant="flat" @click="openManualIrmaf(form.weborderClean || form.weborderRaw)">
                      <v-icon start>mdi-plus</v-icon>Manuell als IRMAF erfassen
                    </v-btn>
                  </div>
                </v-alert>

                <!-- Abschluss: alle ausgewählten Pakete erfassen -->
                <div v-if="selectedItems.length > 0" class="step-box result-box">
                  <div class="d-flex align-center flex-wrap ga-1">
                    <v-icon class="mr-1">mdi-package-variant-closed</v-icon>
                    <strong>{{ selectedItems.length }}</strong> Paket(e) ausgewählt
                    <template v-if="resultSummary.length">
                      <v-chip
                        v-for="s in resultSummary"
                        :key="s.result"
                        :color="resultColorOf(s.result)"
                        size="x-small"
                        class="ml-1"
                      >{{ s.count }}× {{ resultLabelOf(s.result) }}</v-chip>
                    </template>
                    <v-spacer></v-spacer>
                    <v-btn
                      color="success"
                      :disabled="!canSubmit"
                      @click="submitRetoure"
                    >{{ selectedItems.length > 1 ? selectedItems.length + ' Pakete erfassen' : 'Erfassen' }}</v-btn>
                  </div>
                  <div v-if="!canSubmit" class="text-caption text-medium-emphasis mt-1">
                    Für jedes Paket sind CMMF, Trackingnummer, Menge und eine Klassifizierung (A/B/IRMAF) erforderlich.
                  </div>

                  <!-- Ziel-Palette wählen, wenn mehrere offene Paletten des Typs existieren -->
                  <v-row v-if="needPalettenWahl" dense class="mt-2">
                    <v-col v-if="hatA && offenePalettenA.length > 1" cols="12" sm="6">
                      <v-select
                        v-model="zielPaletteA"
                        :items="palettenOptions('A')"
                        item-title="title"
                        item-value="value"
                        label="Ziel-Palette A-Ware"
                        density="compact"
                        hide-details
                      ></v-select>
                    </v-col>
                    <v-col v-if="hatB && offenePalettenB.length > 1" cols="12" sm="6">
                      <v-select
                        v-model="zielPaletteB"
                        :items="palettenOptions('B')"
                        item-title="title"
                        item-value="value"
                        label="Ziel-Palette B-Ware"
                        density="compact"
                        hide-details
                      ></v-select>
                    </v-col>
                  </v-row>
                </div>

                <div class="text-right mt-2">
                  <v-btn variant="text" size="small" @click="resetForm">Zurücksetzen</v-btn>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Positionen der gewählten Palette – volle Breite unter dem Wizard -->
        <v-row v-if="activePalette" class="mt-0">
          <v-col cols="12">
            <v-card>
              <v-card-title class="text-subtitle-2 d-flex align-center py-2">
                <v-avatar :color="activePalette.paletten_typ === 'A' ? 'teal' : 'blue'" size="24" class="mr-2">
                  {{ activePalette.paletten_typ }}
                </v-avatar>
                {{ activePalette.paletten_nr }} – Positionen
                <span class="text-caption text-medium-emphasis ml-2">({{ (activePalette.positionen || []).length }} Pos. · {{ activePalette.gesamtgewicht }} kg)</span>
                <v-spacer></v-spacer>
                <v-btn icon size="x-small" variant="text" @click="activePalette = null">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </v-card-title>
              <v-divider></v-divider>
              <v-data-table
                :headers="posHeaders"
                :items="activePalette.positionen || []"
                density="compact"
                :items-per-page="10"
              >
                <template #[`item.cmmf`]="{ item }">
                  <span :class="{ 'irmaf-pos': Number(item.kein_output) === 1 }">
                    <v-icon v-if="Number(item.kein_output) === 1" size="x-small" color="orange" class="mr-1" title="Aus IRMAF-Liste">mdi-asterisk</v-icon>
                    {{ fmtCmmf(item.cmmf) }}
                  </span>
                </template>
                <template #[`item.ware_klasse`]="{ item }">
                  <v-chip v-if="item.ware_klasse" size="x-small"
                    :color="item.ware_klasse==='A'?'teal':item.ware_klasse==='B'?'blue':'orange'">
                    {{ item.ware_klasse }}
                  </v-chip>
                </template>
                <template #[`item.return_date`]="{ item }">
                  <div class="d-flex align-center">
                    <span :class="item.return_date ? 'text-warning font-weight-medium' : 'text-medium-emphasis'">
                      {{ fmtEingang(item) }}
                    </span>
                    <v-btn
                      icon size="x-small" variant="text" color="warning" class="ml-1"
                      title="Eingangsdatum (RETURN_DATE) ändern"
                      @click="openDateDialog(item)"
                    >
                      <v-icon size="small">mdi-calendar-edit</v-icon>
                    </v-btn>
                  </div>
                </template>
                <template #[`item.actions`]="{ item }">
                  <v-btn
                    icon size="x-small" variant="text" color="orange"
                    title="Position bearbeiten (Tracking, Grund, CMMF)"
                    @click="openEditPosDialog(item)"
                  >
                    <v-icon>mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn
                    icon size="x-small" variant="text" color="primary"
                    title="Auf andere Palette verschieben"
                    @click="openMoveDialog(item)"
                  >
                    <v-icon>mdi-swap-horizontal</v-icon>
                  </v-btn>
                  <v-btn icon size="x-small" variant="text" color="error" @click="removePosition(item.id)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </template>
              </v-data-table>
            </v-card>
          </v-col>
        </v-row>

      </v-window-item>

      <!-- ============================ PALETTEN ============================ -->
      <v-window-item value="paletten">
        <v-card>
          <v-card-title class="d-flex align-center">
            Paletten-Übersicht
            <v-spacer></v-spacer>
            <v-btn-toggle v-model="palettenTab" density="compact" mandatory color="teal">
              <v-btn value="aktiv" size="small">Aktiv</v-btn>
              <v-btn value="archiv" size="small">Archiv</v-btn>
            </v-btn-toggle>
          </v-card-title>
          <v-data-table
            :headers="palettenHeaders"
            :items="paletten"
            density="compact"
          >
            <template #[`item.paletten_typ`]="{ item }">
              <v-chip :color="item.paletten_typ === 'A' ? 'teal' : 'blue'" size="x-small">{{ item.paletten_typ }}</v-chip>
            </template>
            <template #[`item.status`]="{ item }">
              <v-chip :color="statusColor(item.status)" size="x-small">{{ statusText(item.status) }}</v-chip>
            </template>
            <template #[`item.actions`]="{ item }">
              <v-btn
                size="x-small" color="teal" variant="tonal"
                title="Palettenschein erneut drucken"
                @click="printPalettenschein(item)"
              >
                <v-icon start size="small">mdi-printer</v-icon>
                Schein
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-window-item>

      <!-- ============================ IRMAF ============================ -->
      <v-window-item value="irmaf">
        <v-card>
          <v-card-title class="d-flex align-center">
            IRMAF-Liste
            <v-spacer></v-spacer>
            <v-btn color="orange-darken-2" size="small" variant="flat" class="mr-3" @click="openManualIrmaf()">
              <v-icon start>mdi-plus</v-icon>Manueller Eintrag
            </v-btn>
            <v-btn-toggle v-model="irmafTab" density="compact" mandatory color="orange">
              <v-btn value="aktiv" size="small">Offen</v-btn>
              <v-btn value="archiv" size="small">Archiv</v-btn>
            </v-btn-toggle>
          </v-card-title>
          <v-data-table :headers="irmafHeaders" :items="irmafListe" density="compact">
            <template #[`item.notiz`]="{ item }">
              <span>{{ item.notiz || '–' }}</span>
              <span v-if="item.sepas_auftragsnummer" class="text-caption text-purple"> · Sepas {{ item.sepas_auftragsnummer }}</span>
              <span v-if="item.return_reason_code" class="text-caption text-medium-emphasis"> · {{ item.return_reason_code }}</span>
            </template>
            <template #[`item.cmmf`]="{ item }">
              {{ fmtCmmf(item.cmmf) }}
            </template>
            <template #[`item.status`]="{ item }">
              <v-chip v-if="item.status === 'abgeschlossen'" :color="abschlussColor(item.abschluss_typ)" size="x-small">
                {{ abschlussLabel(item.abschluss_typ) }}<span v-if="item.ware_klasse"> · {{ item.ware_klasse }}</span>
              </v-chip>
              <v-chip v-else color="orange" size="x-small">Offen</v-chip>
            </template>
            <template #[`item.actions`]="{ item }">
              <v-btn
                v-if="item.status !== 'abgeschlossen'"
                size="x-small" color="teal" variant="flat"
                @click="openCloseDialog(item)"
              >
                <v-icon start size="small">mdi-clipboard-check</v-icon>
                Weiter erfassen
              </v-btn>
              <v-btn
                v-else-if="item.abschluss_typ !== 'rueckversand_kunde'"
                size="x-small" variant="outlined"
                @click="reopenIrmaf(item)"
              >
                <v-icon start size="small">mdi-lock-open-variant</v-icon>
                Wieder öffnen
              </v-btn>
              <span v-else class="text-caption text-medium-emphasis">—</span>
            </template>
          </v-data-table>
        </v-card>
      </v-window-item>

      <!-- ============================ IMPORT ============================ -->
      <v-window-item value="import">
        <v-card>
          <v-card-title>SalesHistory importieren</v-card-title>
          <v-card-text>
            <v-alert type="info" density="compact" class="mb-2">
              Zeilen aus Excel kopieren und hier einfügen (Strg+A → Strg+C in Excel, dann Strg+V hier).
              Kopfzeilen werden automatisch erkannt und übersprungen – egal ob mit oder ohne die ersten 3 Excel-Zeilen eingefügt.
              Bereits vorhandene Zeilen werden aktualisiert (kein Duplikat).
              <span v-if="salesStatus.gesamt">Aktuell {{ salesStatus.gesamt }} Datensätze, letzter Import: {{ salesStatus.letzter_import }}.</span>
            </v-alert>

            <!-- kein v-model → kein Reaktivitäts-Freeze bei 439k Zeilen -->
            <textarea
              ref="importTextArea"
              placeholder="Hier einfügen (Strg+V) …"
              rows="8"
              style="width:100%; font-size:12px; font-family:monospace; border:1px solid #ccc; border-radius:4px; padding:8px; resize:vertical;"
            ></textarea>

            <div class="d-flex align-center gap-3 flex-wrap mt-3">
              <v-btn color="teal" :loading="importing" @click="importSales">
                <v-icon start>mdi-upload</v-icon>
                Importieren
              </v-btn>
              <v-btn variant="text" size="small" @click="clearImport">Zurücksetzen</v-btn>
              <v-btn
                v-if="fehlzeilen.length > 0"
                color="orange"
                variant="tonal"
                size="small"
                @click="fehlzeilenDialog = true"
              >
                <v-icon start>mdi-alert</v-icon>
                {{ fehlzeilen.length }} Fehlzeile{{ fehlzeilen.length !== 1 ? 'n' : '' }} ansehen
              </v-btn>
              <v-progress-linear
                v-if="importProgress > 0 && importProgress < 100"
                :model-value="importProgress"
                color="teal"
                rounded
                height="8"
                style="width:200px"
              ></v-progress-linear>
              <span v-if="importResult" class="text-caption">{{ importResult }}</span>
            </div>
          </v-card-text>
        </v-card>
      </v-window-item>

      <!-- ============================ MASTERLISTE ============================ -->
      <v-window-item value="master">
        <v-card>
          <v-card-title>Artikel-Masterliste importieren</v-card-title>
          <v-card-text>
            <v-alert type="info" density="compact" class="mb-2">
              Tab-getrennte Zeilen einfügen. Spalten-Reihenfolge: <strong>SKU&nbsp;·&nbsp;EAN&nbsp;·&nbsp;CMMF&nbsp;·&nbsp;Bezeichnung</strong>
              (Kopfzeile optional). Vorhandene Einträge werden über SKU + EAN + CMMF aktualisiert (Upsert).
              <span v-if="masterStatus.gesamt">Aktuell {{ masterStatus.gesamt }} Einträge ({{ masterStatus.skus }} SKUs), letzter Import: {{ masterStatus.letzter_import }}.</span>
            </v-alert>
            <v-textarea
              v-model="masterText"
              label="Masterliste (Tab-getrennt: SKU · EAN · CMMF · Bezeichnung)"
              rows="6"
              auto-grow
            ></v-textarea>
            <v-btn color="teal" :loading="masterImporting" @click="importMaster">
              <v-icon start>mdi-database-import</v-icon> Importieren
            </v-btn>
            <span v-if="masterResult" class="ml-3 text-caption">{{ masterResult }}</span>

            <v-divider class="my-4" />

            <!-- Artikel-Masterliste ansehen -->
            <div class="d-flex align-center gap-2 mb-3">
              <div class="text-subtitle-2">Artikel-Masterliste ansehen</div>
              <v-btn color="teal" variant="tonal" size="small" :loading="loadingDtcKatalog" @click="loadDtcKatalog">
                <v-icon start>mdi-refresh</v-icon>Laden
              </v-btn>
            </div>
            <v-text-field
              v-if="dtcKatalogItems.length > 0"
              v-model="dtcKatalogSuche"
              label="Suche nach SKU, CMMF, EAN oder Bezeichnung"
              prepend-inner-icon="mdi-magnify"
              variant="outlined"
              density="compact"
              clearable
              class="mb-3"
            ></v-text-field>
            <v-data-table
              v-if="dtcKatalogItems.length > 0"
              :headers="[
                { title: 'SKU', key: 'sku', sortable: true },
                { title: 'CMMF', key: 'cmmf', sortable: true },
                { title: 'EAN', key: 'ean', sortable: true },
                { title: 'Bezeichnung', key: 'bezeichnung', sortable: true },
                { title: 'Aktualisiert', key: 'aktualisiert_am', sortable: true },
              ]"
              :items="dtcKatalogGefiltert"
              :loading="loadingDtcKatalog"
              density="compact"
              :items-per-page="25"
              class="elevation-1"
            >
              <template #[`item.cmmf`]="{ item }">
                <span class="text-monospace">{{ fmtCmmf(item.cmmf) }}</span>
              </template>
              <template #[`item.aktualisiert_am`]="{ item }">
                <span class="text-caption text-medium-emphasis">{{ item.aktualisiert_am ? formatDateTime(item.aktualisiert_am) : '—' }}</span>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-window-item>

      <!-- ============================ SALESHISTORY-SUCHE ============================ -->
      <v-window-item value="artikelkatalog">
        <v-card>
          <v-card-title>SalesHistory durchsuchen</v-card-title>
          <v-card-text>
            <v-alert type="info" density="compact" class="mb-3">
              Serverseitige Suche – max. 500 Ergebnisse. Ohne Suchbegriff werden die 500 zuletzt importierten Zeilen angezeigt.
              <span v-if="salesStatus.gesamt"> Aktuell {{ salesStatus.gesamt }} Datensätze in der Datenbank.</span>
            </v-alert>
            <v-row dense align="center">
              <v-col cols="12" sm="3">
                <v-select
                  v-model="shSuchFeld"
                  :items="[
                    { title: 'Alle Felder', value: 'all' },
                    { title: 'Weborder-ID', value: 'weborder' },
                    { title: 'PFS-Order-ID', value: 'pfs' },
                    { title: 'SKU', value: 'sku' },
                    { title: 'CMMF', value: 'cmmf' },
                    { title: 'Tracking', value: 'tracking' },
                  ]"
                  label="Suchfeld"
                  density="compact"
                  variant="outlined"
                  hide-details
                ></v-select>
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field
                  v-model="shSuchBegriff"
                  label="Suchbegriff"
                  density="compact"
                  variant="outlined"
                  hide-details
                  clearable
                  @keyup.enter="sucheSalesHistory"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="2">
                <v-text-field v-model="shDateFrom" type="date" label="Von" density="compact" variant="outlined" hide-details></v-text-field>
              </v-col>
              <v-col cols="12" sm="2">
                <v-text-field v-model="shDateTo" type="date" label="Bis" density="compact" variant="outlined" hide-details></v-text-field>
              </v-col>
              <v-col cols="12" sm="1">
                <v-btn color="teal" :loading="loadingShSuche" @click="sucheSalesHistory" block>
                  <v-icon>mdi-magnify</v-icon>
                </v-btn>
              </v-col>
            </v-row>
            <div v-if="shErgebnisse.length > 0" class="text-caption text-medium-emphasis mt-2 mb-1">
              {{ shErgebnisse.length === 500 ? 'Maximal 500 Ergebnisse angezeigt – Suche verfeinern.' : `${shErgebnisse.length} Ergebnis(se)` }}
            </div>
            <v-data-table
              :headers="[
                { title: 'Weborder', key: 'weborder_id', sortable: true },
                { title: 'PFS', key: 'pfs_order_id', sortable: true },
                { title: 'Datum', key: 'order_date', sortable: true },
                { title: 'Typ', key: 'order_type_norm', sortable: true },
                { title: 'SKU', key: 'sku', sortable: true },
                { title: 'CMMF', key: 'cmmf', sortable: true },
                { title: 'Produkt', key: 'product_desc', sortable: false },
                { title: 'Tracking', key: 'tracking_source', sortable: false },
                { title: 'Datumsregel', key: 'belgien_flag', sortable: true },
                { title: 'Importiert am', key: 'aktualisierungsdatum', sortable: true },
              ]"
              :items="shErgebnisse"
              :loading="loadingShSuche"
              density="compact"
              :items-per-page="25"
              class="elevation-1 mt-2"
            >
              <template #[`item.cmmf`]="{ item }">
                <span class="text-monospace">{{ fmtCmmf(item.cmmf) }}</span>
              </template>
              <template #[`item.belgien_flag`]="{ item }">
                <v-chip v-if="isRueckKundeRow(item)" color="purple" size="x-small">Rück Kunde</v-chip>
                <v-chip v-else-if="item.belgien_flag" color="orange" size="x-small">IRMAF</v-chip>
                <span v-else class="text-grey">—</span>
              </template>
              <template #[`item.order_type_norm`]="{ item }">
                <v-chip :color="item.order_type_norm === 'Return' ? 'red-lighten-3' : 'blue-lighten-3'" size="x-small">
                  {{ item.order_type_norm || '—' }}
                </v-chip>
              </template>
              <template #[`item.aktualisierungsdatum`]="{ item }">
                <span class="text-caption text-medium-emphasis">{{ item.aktualisierungsdatum ? formatDateTime(item.aktualisierungsdatum) : '—' }}</span>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-window-item>

      <!-- ============================ TAGESÜBERSICHT ============================ -->
      <v-window-item value="tagesuebersicht">
        <v-card>
          <v-card-title>Tagesübersicht – Erfassungen</v-card-title>
          <v-card-text>
            <v-alert type="info" density="compact" class="mb-3">
              Aufnahmen im gewählten Zeitraum (Paletten-Positionen und IRMAF-Einträge), chronologisch sortiert.
              <strong>Output-CSV „Ja“</strong>: Positionen ohne Ausschluss-Flag sowie IRMAF-Fälle, die per Vereinnahmung mit Output-Aufnahme abgeschlossen wurden.
              Die Spalte <strong>CSV-Datei</strong> zeigt die zugehörige(n) Export-Datei(en) aus dem Output-Log (falls vorhanden).
            </v-alert>
            <v-row dense align="center">
              <v-col cols="12" sm="2">
                <v-text-field
                  v-model="tagDateFrom"
                  type="date"
                  label="Von"
                  density="compact"
                  variant="outlined"
                  hide-details
                  :disabled="tagAlleTage"
                  @update:model-value="loadTageserfassung"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="2">
                <v-text-field
                  v-model="tagDateTo"
                  type="date"
                  label="Bis"
                  density="compact"
                  variant="outlined"
                  hide-details
                  :disabled="tagAlleTage"
                  @update:model-value="loadTageserfassung"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="2">
                <v-checkbox
                  v-model="tagAlleTage"
                  label="Alle Tage"
                  density="compact"
                  hide-details
                  @update:model-value="loadTageserfassung"
                ></v-checkbox>
              </v-col>
              <v-col cols="12" sm="3">
                <v-select
                  v-model="tagOutputFilter"
                  :items="[
                    { title: 'Alle', value: 'all' },
                    { title: 'In Output-CSV', value: 'in_output' },
                    { title: 'Nicht in Output-CSV', value: 'excluded' },
                  ]"
                  label="Output-CSV"
                  density="compact"
                  variant="outlined"
                  hide-details
                  @update:model-value="loadTageserfassung"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="3">
                <v-select
                  v-model="tagQuelleFilter"
                  :items="[
                    { title: 'Alle Quellen', value: 'all' },
                    { title: 'Nur Positionen', value: 'position' },
                    { title: 'Nur IRMAF', value: 'irmaf' },
                  ]"
                  label="Quelle"
                  density="compact"
                  variant="outlined"
                  hide-details
                  @update:model-value="loadTageserfassung"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="tagSuchBegriff"
                  label="Suche (Weborder, SKU, CMMF …)"
                  density="compact"
                  variant="outlined"
                  hide-details
                  clearable
                  prepend-inner-icon="mdi-magnify"
                  @keyup.enter="loadTageserfassung"
                  @click:clear="loadTageserfassung"
                ></v-text-field>
              </v-col>
            </v-row>
            <div class="d-flex align-center gap-2 flex-wrap mt-3 mb-2">
              <v-btn color="teal" :loading="loadingTageserfassung" @click="loadTageserfassung">
                <v-icon start>mdi-refresh</v-icon>Laden
              </v-btn>
              <v-chip v-if="tagSummary.gesamt != null" size="small" color="grey">{{ tagSummary.gesamt }} gesamt</v-chip>
              <v-chip v-if="tagSummary.positionen != null" size="small" color="teal">{{ tagSummary.positionen }} Positionen</v-chip>
              <v-chip v-if="tagSummary.irmaf != null" size="small" color="orange">{{ tagSummary.irmaf }} IRMAF</v-chip>
              <v-chip v-if="tagSummary.in_output != null" size="small" color="success">{{ tagSummary.in_output }} in Output</v-chip>
              <v-chip v-if="tagSummary.excluded != null" size="small" color="red-lighten-2">{{ tagSummary.excluded }} ausgeschlossen</v-chip>
            </div>
            <v-data-table
              :headers="tagHeaders"
              :items="tagErgebnisse"
              :loading="loadingTageserfassung"
              density="compact"
              :items-per-page="25"
              class="elevation-1"
            >
              <template #[`item.erfasst_datum`]="{ item }">
                <span class="text-caption">{{ formatDateTime(item.erfasst_datum) }}</span>
              </template>
              <template #[`item.quelle`]="{ item }">
                <template v-if="item.quelle === 'position'">
                  <v-chip color="teal" size="x-small">Position</v-chip>
                </template>
                <template v-else>
                  <v-tooltip :text="tagIrmafTooltip(item)" location="top">
                    <template #activator="{ props }">
                      <v-chip v-bind="props" :color="tagIrmafColor(item)" size="x-small">
                        {{ tagIrmafLabel(item) }}
                      </v-chip>
                    </template>
                  </v-tooltip>
                </template>
              </template>
              <template #[`item.cmmf`]="{ item }">
                <span class="text-monospace">{{ fmtCmmf(item.cmmf) }}</span>
              </template>
              <template #[`item.in_output_csv`]="{ item }">
                <v-chip :color="item.in_output_csv ? 'success' : 'red-lighten-2'" size="x-small">
                  {{ item.in_output_csv ? 'Ja' : 'Nein' }}
                </v-chip>
              </template>
              <template #[`item.output_csv_datei`]="{ item }">
                <span
                  v-if="item.output_csv_datei"
                  class="text-caption text-monospace"
                  :title="item.output_csv_datei"
                >
                  {{ item.output_csv_datei }}
                </span>
                <span v-else class="text-caption text-medium-emphasis">—</span>
              </template>
              <template #[`item.ware_klasse`]="{ item }">
                <v-chip
                  v-if="item.abschluss_typ === 'rueckversand_kunde' || item.ware_klasse === 'RUECK'"
                  color="purple"
                  size="x-small"
                >
                  Rück Kunde
                </v-chip>
                <v-chip
                  v-else-if="item.ware_klasse && item.ware_klasse !== 'IRMAF'"
                  :color="item.ware_klasse === 'A' ? 'teal' : 'blue'"
                  size="x-small"
                >
                  {{ item.ware_klasse }}
                </v-chip>
                <span v-else-if="item.quelle === 'irmaf'" class="text-caption text-medium-emphasis">—</span>
                <span v-else>—</span>
              </template>
              <template #[`item.paletten_nr`]="{ item }">
                <span v-if="item.sepas_auftragsnummer" class="text-caption">
                  Sepas {{ item.sepas_auftragsnummer }}
                </span>
                <span v-else class="text-caption">{{ item.paletten_nr || '—' }}</span>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-window-item>

      <!-- ============================ OUTPUT ============================ -->
      <v-window-item value="output">
        <v-card>
          <v-card-title>Output-CSV (DHL_retconf_DACH)</v-card-title>
          <v-card-text>
            <v-row dense align="center">
              <v-col cols="12" sm="4">
                <v-text-field v-model="outputDate" type="date" label="Datum" density="compact"></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <v-btn color="teal" @click="downloadOutput">
                  <v-icon start>mdi-file-download</v-icon> CSV herunterladen
                </v-btn>
              </v-col>
            </v-row>
            <v-divider class="my-3"></v-divider>
            <div class="text-subtitle-2 mb-2">Letzte Exporte</div>
            <v-data-table :headers="outputHeaders" :items="outputLog" density="compact">
              <template #[`item.actions`]="{ item }">
                <v-btn
                  size="x-small"
                  variant="text"
                  color="teal"
                  title="CSV erneut herunterladen"
                  :loading="downloadingLogId === item.id"
                  @click="downloadOutputLog(item)"
                >
                  <v-icon start size="small">mdi-file-download</v-icon>
                  Download
                </v-btn>
              </template>
              <template #[`item.pfs_status`]="{ item }">
                <template v-if="item.pfs_bestaetigt_am">
                  <v-chip color="success" size="x-small">
                    <v-icon start size="small">mdi-check-circle</v-icon>
                    Hochgeladen
                  </v-chip>
                  <div class="text-caption text-medium-emphasis">
                    {{ item.pfs_bestaetigt_von_name }} · {{ formatDateTime(item.pfs_bestaetigt_am) }}
                  </div>
                </template>
                <v-btn
                  v-else
                  size="x-small"
                  color="teal"
                  variant="flat"
                  :loading="confirmingId === item.id"
                  @click="confirmPfsUpload(item)"
                >
                  <v-icon start size="small">mdi-cloud-check</v-icon>
                  Upload bestätigen
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-window-item>

      <!-- ============================ ADMIN KORREKTUR ============================ -->
      <v-window-item v-if="isAdmin" value="korrektur">
        <v-row>
          <v-col cols="12" lg="7">
            <v-card class="mb-4">
              <v-card-title class="d-flex align-center">
                <v-icon color="red-lighten-2" class="mr-2">mdi-shield-edit</v-icon>
                Umbuchung / Korrektur
              </v-card-title>
              <v-card-text>
                <v-alert type="info" density="compact" class="mb-3">
                  Korrekturen sind nur bis einschließlich <strong>Office-Freigabe</strong> möglich.
                  Output-Anpassungen wirken auf gespeicherte CSV-Snapshots (alte Exporte ohne Snapshot: Live-Stand).
                </v-alert>
                <v-row dense align="center">
                  <v-col cols="12" sm="8">
                    <v-text-field
                      v-model="korrekturSearch"
                      label="Weborder, Tracking, Palette oder Sepas"
                      density="compact"
                      prepend-inner-icon="mdi-magnify"
                      clearable
                      hide-details
                      @keyup.enter="searchKorrektur"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <v-btn color="teal" :loading="korrekturLoading" @click="searchKorrektur">
                      Suchen
                    </v-btn>
                  </v-col>
                </v-row>
                <v-data-table
                  :headers="korrekturHeaders"
                  :items="korrekturResults"
                  density="compact"
                  class="mt-4"
                  :loading="korrekturLoading"
                >
                  <template #[`item.palette_status`]="{ item }">
                    <v-chip :color="statusColor(item.palette_status)" size="x-small">
                      {{ statusText(item.palette_status) }}
                    </v-chip>
                  </template>
                  <template #[`item.in_output_csv`]="{ item }">
                    <v-chip :color="item.in_output_csv ? 'success' : 'red-lighten-2'" size="x-small">
                      {{ item.in_output_csv ? 'Ja' : 'Nein' }}
                    </v-chip>
                  </template>
                  <template #[`item.korrektur_erlaubt`]="{ item }">
                    <v-icon :color="item.korrektur_erlaubt ? 'success' : 'error'" size="small">
                      {{ item.korrektur_erlaubt ? 'mdi-check' : 'mdi-close' }}
                    </v-icon>
                  </template>
                  <template #[`item.actions`]="{ item }">
                    <v-btn
                      size="x-small"
                      color="teal"
                      variant="text"
                      :disabled="!item.korrektur_erlaubt"
                      @click="selectKorrekturPosition(item)"
                    >
                      Korrektur
                    </v-btn>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>

            <v-card v-if="korrekturForm.position_id">
              <v-card-title>Korrektur: {{ korrekturForm.weborder_id }}</v-card-title>
              <v-card-text>
                <v-row dense>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="korrekturForm.tracking_number"
                      label="Trackingnummer"
                      density="compact"
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-select
                      v-model="korrekturForm.return_reason_code"
                      :items="activeReasonItems"
                      item-title="label"
                      item-value="code"
                      label="Rücksendegrund"
                      density="compact"
                      hide-details
                    ></v-select>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="korrekturForm.cmmf"
                      label="CMMF"
                      density="compact"
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="3">
                    <v-text-field
                      v-model.number="korrekturForm.menge"
                      type="number"
                      min="1"
                      label="Menge"
                      density="compact"
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="3">
                    <v-text-field
                      v-model="korrekturForm.return_date"
                      type="date"
                      label="RETURN_DATE"
                      density="compact"
                      hide-details
                      clearable
                    ></v-text-field>
                  </v-col>
                </v-row>

                <v-divider class="my-4"></v-divider>
                <div class="text-subtitle-2 mb-2">Bestandsbewegung</div>
                <v-radio-group v-model="korrekturForm.bewegungTyp" density="compact" hide-details>
                  <v-radio label="Ohne Verschiebung (nur logische Korrektur)" value="none"></v-radio>
                  <v-radio label="Mit Verschiebung auf andere Palette" value="move"></v-radio>
                </v-radio-group>
                <v-select
                  v-if="korrekturForm.bewegungTyp === 'move'"
                  v-model="korrekturForm.ziel_palette_id"
                  :items="korrekturPaletten"
                  item-title="label"
                  item-value="id"
                  label="Ziel-Palette"
                  density="compact"
                  class="mt-2"
                  hide-details
                ></v-select>

                <v-divider class="my-4"></v-divider>
                <v-checkbox
                  v-model="korrekturForm.output_anpassen"
                  label="Output anpassen"
                  density="compact"
                  hide-details
                ></v-checkbox>
                <div v-if="korrekturForm.output_anpassen" class="ml-6 mt-2">
                  <v-checkbox
                    v-model="korrekturForm.aus_output_entfernen"
                    label="Aus bisherigem Output entfernen"
                    density="compact"
                    hide-details
                    :disabled="!korrekturForm.in_output_csv"
                  ></v-checkbox>
                  <v-checkbox
                    v-model="korrekturForm.in_output_aufnehmen"
                    label="In Output aufnehmen / einfügen"
                    density="compact"
                    hide-details
                    class="mt-2"
                  ></v-checkbox>
                  <div v-if="korrekturForm.in_output_aufnehmen" class="ml-4 mt-2">
                    <v-radio-group
                      v-model="korrekturForm.ziel_output_modus"
                      density="compact"
                      hide-details
                    >
                      <v-radio
                        label="Heute anfügen (erscheint beim nächsten CSV-Download für heute)"
                        value="heute"
                      ></v-radio>
                      <v-radio
                        label="In bestehenden Export einfügen"
                        value="export"
                      ></v-radio>
                    </v-radio-group>
                    <v-select
                      v-if="korrekturForm.ziel_output_modus === 'export'"
                      v-model="korrekturForm.ziel_output_log_id"
                      :items="korrekturOutputOptions"
                      item-title="label"
                      item-value="id"
                      label="Ziel-Export"
                      density="compact"
                      class="mt-2"
                      hide-details
                      :loading="korrekturOutputLoading"
                      clearable
                    ></v-select>
                    <div class="text-caption text-medium-emphasis mt-1">
                      z.&nbsp;B. Export der Ziel-Palette / eines früheren Tages – Zeilen werden an diesen Snapshot angehängt.
                    </div>
                  </div>
                  <div v-if="korrekturForm.output_log_id" class="text-caption text-medium-emphasis mt-2">
                    Bisheriger Export der Position: {{ korrekturForm.output_csv_datei || korrekturForm.output_log_id }}
                    <span v-if="korrekturForm.hat_output_snapshot"> · Snapshot vorhanden</span>
                    <span v-else> · Kein Snapshot (Live-Stand)</span>
                  </div>
                  <v-btn
                    v-if="downloadableKorrekturOutputId"
                    size="small"
                    variant="text"
                    color="teal"
                    class="mt-2"
                    :loading="korrekturCsvLoading"
                    @click="downloadKorrekturCsv"
                  >
                    <v-icon start>mdi-file-download</v-icon>
                    Korrigierte CSV
                  </v-btn>
                </div>

                <v-textarea
                  v-model="korrekturForm.kommentar"
                  label="Kommentar (Pflicht)"
                  rows="2"
                  density="compact"
                  class="mt-4"
                  hide-details
                ></v-textarea>

                <div class="d-flex ga-2 mt-4">
                  <v-btn variant="text" @click="resetKorrekturForm">Abbrechen</v-btn>
                  <v-btn color="red-lighten-2" :loading="korrekturSaving" @click="submitKorrektur">
                    Korrektur speichern
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" lg="5">
            <v-card>
              <v-card-title>Weborder-Historie</v-card-title>
              <v-card-text>
                <div v-if="!korrekturHistoryWeborder" class="text-medium-emphasis">
                  Position auswählen oder suchen, um die Historie zu laden.
                </div>
                <div
                  v-else-if="korrekturHistory.length === 0"
                  class="text-medium-emphasis text-body-2"
                >
                  Noch keine Korrekturen für diese Weborder.
                </div>
                <v-timeline v-else side="end" density="compact" truncate-line="both" class="korrektur-timeline">
                  <v-timeline-item
                    v-for="entry in korrekturHistory"
                    :key="entry.id"
                    dot-color="teal"
                    size="small"
                  >
                    <div class="text-caption text-medium-emphasis">
                      {{ formatDateTime(entry.erstelldatum) }} · {{ entry.user_name || '—' }}
                    </div>
                    <div class="text-body-2">{{ entry.kommentar }}</div>
                    <div v-if="entry.aenderungen" class="text-caption mt-1">
                      <span v-if="entry.aenderungen.before?.paletten_nr !== entry.aenderungen.after?.paletten_nr">
                        Palette: {{ entry.aenderungen.before?.paletten_nr }} → {{ entry.aenderungen.after?.paletten_nr }}
                      </span>
                      <span v-if="entry.aenderungen.before?.tracking_number !== entry.aenderungen.after?.tracking_number">
                        · Tracking geändert
                      </span>
                      <span v-if="entry.aenderungen.before?.kein_output !== entry.aenderungen.after?.kein_output">
                        · Output: {{ entry.aenderungen.after?.kein_output ? 'ausgeschlossen' : 'aufgenommen' }}
                      </span>
                    </div>
                    <div v-if="entry.output_aenderung" class="text-caption text-teal mt-1">
                      Output angepasst
                    </div>
                  </v-timeline-item>
                </v-timeline>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-window-item>

      <!-- ============================ SPERREN (Admin) ============================ -->
      <v-window-item v-if="isAdmin" value="sperren">
        <v-row>
          <v-col cols="12" lg="5">
            <v-card>
              <v-card-title class="d-flex align-center">
                <v-icon color="red-lighten-2" class="mr-2">mdi-lock-alert</v-icon>
                Weborder sperren
              </v-card-title>
              <v-card-text>
                <v-alert type="info" density="compact" class="mb-3">
                  Gesperrte Weborders können in der Erfassung nicht mehr gesucht oder gebucht werden.
                  Bestehende Paletten-Positionen bleiben bearbeitbar.
                </v-alert>
                <v-text-field
                  v-model="sperreForm.weborder_id"
                  label="Weborder-ID"
                  density="compact"
                  hide-details
                  class="mb-3"
                  @keyup.enter="sperreWeborder"
                ></v-text-field>
                <v-textarea
                  v-model="sperreForm.grund"
                  label="Grund (optional)"
                  density="compact"
                  rows="3"
                  hide-details
                  class="mb-3"
                ></v-textarea>
                <v-btn color="red-lighten-2" :loading="sperreSaving" :disabled="!sperreForm.weborder_id.trim()" @click="sperreWeborder">
                  Sperren
                </v-btn>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" lg="7">
            <v-card>
              <v-card-title class="d-flex align-center">
                Aktive Sperren
                <v-spacer></v-spacer>
                <v-btn size="small" variant="text" :loading="sperreLoading" @click="loadSperren">
                  <v-icon start>mdi-refresh</v-icon>Aktualisieren
                </v-btn>
              </v-card-title>
              <v-card-text>
                <v-data-table
                  :headers="sperreHeaders"
                  :items="sperreListe"
                  density="compact"
                  :loading="sperreLoading"
                >
                  <template #[`item.grund`]="{ item }">
                    {{ item.grund || '—' }}
                  </template>
                  <template #[`item.gesperrt_am`]="{ item }">
                    {{ formatDateTime(item.gesperrt_am) }}
                  </template>
                  <template #[`item.actions`]="{ item }">
                    <v-btn
                      size="x-small"
                      color="teal"
                      variant="text"
                      :loading="sperreUnlockingId === item.id"
                      @click="entsperreWeborder(item)"
                    >
                      Entsperren
                    </v-btn>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-window-item>
    </v-window>

    <!-- ============================ MANUELLER IRMAF-EINTRAG ============================ -->
    <v-dialog v-model="manualIrmaf.open" max-width="640" persistent>
      <v-card>
        <v-card-title class="bg-orange-darken-2 text-white d-flex align-center">
          <v-icon class="mr-2">mdi-alert-circle-plus</v-icon>
          Manueller IRMAF-Eintrag
          <v-spacer />
          <v-btn icon="mdi-close" variant="text" color="white" @click="manualIrmaf.open = false" />
        </v-card-title>
        <v-card-text class="pa-4">
          <v-alert type="info" density="compact" class="mb-3">
            Für Bestellungen, die nicht in der SalesHistory vorhanden sind. Pflichtfelder: Weborder-ID und IRMAF-ID.
          </v-alert>
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-text-field v-model="manualIrmaf.weborder_id" label="Weborder-ID *" density="compact" variant="outlined" :error="!manualIrmaf.weborder_id" hide-details></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field v-model="manualIrmaf.irmaf_id" label="IRMAF-ID *" density="compact" variant="outlined" :error="!manualIrmaf.irmaf_id" hide-details></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-autocomplete
                v-model="manualIrmaf.sku"
                :items="skuSuggestions"
                :loading="skuLoading"
                item-title="title"
                item-value="sku"
                label="SKU"
                density="compact"
                variant="outlined"
                hide-details
                clearable
                no-filter
                hide-no-data
                :menu-props="{ maxHeight: 300 }"
                @update:search="onSkuSearch"
                @update:model-value="onSkuSelect"
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field v-model="manualIrmaf.tracking_number" label="Trackingnummer" density="compact" variant="outlined" hide-details></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="manualIrmaf.open = false">Abbrechen</v-btn>
          <v-btn
            color="orange-darken-2"
            variant="flat"
            :loading="manualIrmaf.loading"
            :disabled="!manualIrmaf.weborder_id || !manualIrmaf.irmaf_id"
            @click="submitManualIrmaf"
          >
            <v-icon start>mdi-content-save</v-icon>In IRMAF-Liste speichern
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ============================ FEHLZEILEN DIALOG ============================ -->
    <v-dialog v-model="fehlzeilenDialog" max-width="900" scrollable>
      <v-card>
        <v-card-title class="bg-orange-darken-2 text-white d-flex align-center">
          <v-icon class="mr-2">mdi-alert</v-icon>
          Übersprungene Zeilen ({{ fehlzeilen.length }})
          <v-spacer />
          <v-btn icon="mdi-close" variant="text" color="white" @click="fehlzeilenDialog = false" />
        </v-card-title>
        <v-card-text class="pa-4">
          <v-alert type="info" density="compact" class="mb-3">
            Diese Zeilen wurden beim Import übersprungen, weil Weborder-ID oder PFS-Order-ID fehlen.
            Du kannst die fehlenden Werte hier ergänzen und die Zeilen nachträglich importieren.
          </v-alert>
          <div v-for="(z, i) in fehlzeilen" :key="i" class="mb-4 pa-3 rounded" style="border: 1px solid #f57c00;">
            <div class="d-flex align-center gap-2 mb-2">
              <v-chip color="orange" size="x-small">Zeile {{ z.zeile }}</v-chip>
              <v-chip color="red" size="x-small" variant="tonal">{{ z.fehler }}</v-chip>
              <span class="text-caption text-medium-emphasis">SKU: {{ z.sku || '—' }} · Datum: {{ z.order_date || '—' }}</span>
            </div>
            <v-row dense>
              <v-col cols="12" sm="5">
                <v-text-field
                  v-model="z.weborder_id_edit"
                  label="Weborder-ID *"
                  density="compact"
                  variant="outlined"
                  hide-details
                  :error="!z.weborder_id_edit"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="5">
                <v-text-field
                  v-model="z.pfs_order_id_edit"
                  label="PFS-Order-ID *"
                  density="compact"
                  variant="outlined"
                  hide-details
                  :error="!z.pfs_order_id_edit"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="2" class="d-flex align-center">
                <v-btn
                  color="teal"
                  size="small"
                  variant="flat"
                  :disabled="!z.weborder_id_edit || !z.pfs_order_id_edit"
                  @click="importFehlzeile(z, i)"
                  :loading="z.importing"
                >
                  <v-icon>mdi-upload</v-icon>
                </v-btn>
              </v-col>
            </v-row>
            <div v-if="z.ergebnis" class="mt-1 text-caption" :class="z.ergebnis === 'ok' ? 'text-success' : 'text-error'">
              {{ z.ergebnis === 'ok' ? '✓ Importiert' : z.ergebnis }}
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="fehlzeilenDialog = false">Schließen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ============================ IRMAF-ID DIALOG ============================ -->
    <v-dialog v-model="irmafDialog.open" max-width="560" persistent>
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon start color="orange">mdi-alert-circle</v-icon>
          IRMAF-ID(s) angeben
        </v-card-title>
        <v-card-text>
          <p class="text-body-2 mb-3">
            Diese Position(en) werden auf die IRMAF-Liste gesetzt. Bitte für jeden Eintrag die
            IRMAF-ID aus dem externen System angeben.
          </p>
          <div v-for="(row, i) in irmafDialog.rows" :key="i" class="mb-3">
            <div class="text-caption text-medium-emphasis">
              {{ row.sku }}<span v-if="row.cmmf"> · CMMF {{ fmtCmmf(row.cmmf) }}</span>
            </div>
            <v-text-field
              v-model="row.irmaf_id"
              label="IRMAF-ID"
              density="compact"
              hide-details
              autofocus
            ></v-text-field>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="cancelIrmafDialog">Abbrechen</v-btn>
          <v-btn color="orange" variant="flat" @click="confirmIrmafDialog">Übernehmen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ============================ SEPAS DIALOG (Rueckversand Kunde) ============================ -->
    <v-dialog v-model="sepasDialog.open" max-width="560" persistent>
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon start color="purple">mdi-truck-delivery</v-icon>
          Sepas-Auftragsnummer(n)
        </v-card-title>
        <v-card-text>
          <p class="text-body-2 mb-3">
            Rückversand an den Kunden – bitte für jeden Eintrag die Sepas-Auftragsnummer angeben.
            Es wird nur die Notiz „zurück an den Kunden“ gespeichert (kein Output).
          </p>
          <div v-for="(row, i) in sepasDialog.rows" :key="i" class="mb-3">
            <div class="text-caption text-medium-emphasis">
              {{ row.sku }}<span v-if="row.cmmf"> · CMMF {{ fmtCmmf(row.cmmf) }}</span>
            </div>
            <v-text-field
              v-model="row.sepas"
              label="Sepas-Auftragsnummer *"
              density="compact"
              hide-details
              autofocus
            ></v-text-field>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="cancelSepasDialog">Abbrechen</v-btn>
          <v-btn color="purple" variant="flat" @click="confirmSepasDialog">Übernehmen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ============================ 1-von-N Bestätigung ============================ -->
    <v-dialog v-model="singleSelectConfirm.open" max-width="480" persistent>
      <v-card>
        <v-card-title>Nur ein Paket ausgewählt</v-card-title>
        <v-card-text>
          Zu dieser Weborder gibt es <strong>{{ singleSelectConfirm.total }}</strong> buchbare Pakete,
          aber nur <strong>eines</strong> ist ausgewählt.
          Wirklich nur dieses Paket annehmen?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="resolveSingleSelectConfirm(false)">Abbrechen</v-btn>
          <v-btn color="teal" variant="flat" @click="resolveSingleSelectConfirm(true)">Nur dieses annehmen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ============================ IRMAF-ABSCHLUSS DIALOG ============================ -->
    <v-dialog v-model="closeDialog.open" max-width="560" persistent>
      <v-card v-if="closeDialog.item">
        <v-card-title class="d-flex align-center">
          <v-icon start color="teal">mdi-clipboard-check</v-icon>
          IRMAF-Fall abschließen
        </v-card-title>
        <v-card-text>
          <div class="text-caption text-medium-emphasis mb-3">
            IRMAF-ID {{ closeDialog.item.irmaf_id }} · {{ closeDialog.item.sku }}
            <span v-if="closeDialog.item.cmmf"> · CMMF {{ fmtCmmf(closeDialog.item.cmmf) }}</span>
          </div>

          <v-radio-group v-model="closeDialog.typ" hide-details class="mb-2">
            <v-radio value="vereinnahmung">
              <template #label>
                <div>
                  <strong>Vereinnahmung</strong>
                  <div class="text-caption text-medium-emphasis">
                    Rückerstattung via CCC erfolgt, Gerät bleibt in Solingen → A- oder B-Ware (kein Output)
                  </div>
                </div>
              </template>
            </v-radio>
            <v-radio value="schrott">
              <template #label>
                <div>
                  <strong>Schrott</strong>
                  <div class="text-caption text-medium-emphasis">
                    Gerät soll laut CCC verschrottet werden (kein Output)
                  </div>
                </div>
              </template>
            </v-radio>
            <v-radio value="rueckversand">
              <template #label>
                <div>
                  <strong>Rückversand</strong>
                  <div class="text-caption text-medium-emphasis">
                    Gerät wurde via Sepas an KD zurückgesendet (kein Output)
                  </div>
                </div>
              </template>
            </v-radio>
          </v-radio-group>

          <v-expand-transition>
            <div v-if="closeDialog.typ === 'vereinnahmung'" class="mt-3">
              <v-divider class="mb-3"></v-divider>
              <div class="text-caption text-medium-emphasis mb-2">
                Daten prüfen und Klassifizierung durchlaufen (A oder B):
              </div>
              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="closeWiz.tracking"
                    label="Trackingnummer"
                    density="compact"
                    hide-details
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-select
                    v-model="closeWiz.reason"
                    :items="activeReasonItems"
                    item-title="label"
                    item-value="code"
                    label="Rücksendegrund"
                    density="compact"
                    hide-details
                    @update:model-value="closeWizOnReason"
                  ></v-select>
                </v-col>
              </v-row>

              <!-- Entscheidungsbaum -->
              <div v-if="closeWizNode" class="decision-box pa-2 mt-2">
                <div class="decision-question">{{ closeWizNode.question }}</div>
                <div class="d-flex ga-2 mt-2">
                  <v-btn size="small" color="success" @click="answerFor(closeWiz, true)">Ja</v-btn>
                  <v-btn size="small" color="error" @click="answerFor(closeWiz, false)">Nein</v-btn>
                </div>
              </div>

              <!-- Manuelle Klassifizierung (A/B) -->
              <div v-if="closeWiz.manualChoice" class="decision-box pa-2 mt-2">
                <div class="text-caption mb-1">Klassifizierung wählen:</div>
                <div class="d-flex ga-2 flex-wrap">
                  <v-btn size="small" color="teal" @click="setResultFor(closeWiz, 'A')">A-Ware</v-btn>
                  <v-btn size="small" color="blue" @click="setResultFor(closeWiz, 'B')">B-Ware</v-btn>
                </div>
              </div>

              <!-- Ergebnis -->
              <div v-if="closeWiz.result" class="mt-2">
                <v-chip :color="resultColorOf(closeWiz.result)" size="small">
                  <v-icon start size="small">mdi-check-decagram</v-icon>
                  {{ resultLabelOf(closeWiz.result) }}
                </v-chip>
                <v-btn variant="text" size="x-small" class="ml-1" @click="closeWizReclassify">neu bewerten</v-btn>
                <v-btn variant="text" size="x-small" @click="closeWizManual">manuell wählen</v-btn>
                <span v-if="closeWiz.result === 'IRMAF'" class="text-caption text-error ml-2">
                  IRMAF ist hier nicht zulässig – bitte A oder B wählen.
                </span>
              </div>

              <!-- Output-Option: Standard = kein Output, optional doch in Output -->
              <v-checkbox
                v-if="closeWiz.result === 'A' || closeWiz.result === 'B'"
                v-model="closeWiz.mitOutput"
                density="compact"
                hide-details
                color="teal"
                class="mt-2"
              >
                <template #label>
                  <div>
                    <strong>Doch in Output-CSV aufnehmen</strong>
                    <div class="text-caption text-medium-emphasis">
                      Standardmäßig wird die Vereinnahmung NICHT in die Output-CSV übernommen.
                      Haken setzen, wenn das Gerät doch mit in den Output soll.
                    </div>
                  </div>
                </template>
              </v-checkbox>

              <!-- Ziel-Palette wählen, wenn mehrere offene Paletten des Typs existieren -->
              <v-select
                v-if="closeWizPalettenOptions.length > 2"
                v-model="closeWiz.palette"
                :items="closeWizPalettenOptions"
                item-title="title"
                item-value="value"
                label="Ziel-Palette"
                density="compact"
                hide-details
                class="mt-3"
              ></v-select>
            </div>
          </v-expand-transition>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="closeDialog.open = false">Abbrechen</v-btn>
          <v-btn color="teal" variant="flat" :loading="closeDialog.loading" @click="confirmCloseIrmaf">Abschließen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- ============================ POSITION VERSCHIEBEN DIALOG ============================ -->
    <v-dialog v-model="moveDialog.open" max-width="480">
      <v-card v-if="moveDialog.item">
        <v-card-title class="d-flex align-center">
          <v-icon start color="primary">mdi-swap-horizontal</v-icon>
          Position verschieben
        </v-card-title>
        <v-card-text>
          <div class="text-caption text-medium-emphasis mb-3">
            {{ moveDialog.item.sku }}<span v-if="moveDialog.item.cmmf"> · CMMF {{ fmtCmmf(moveDialog.item.cmmf) }}</span>
            · {{ moveDialog.item.ware_klasse }}-Ware
          </div>
          <v-select
            v-model="moveDialog.ziel"
            :items="moveDialogOptions"
            item-title="title"
            item-value="value"
            label="Ziel-Palette"
            density="compact"
            hide-details
            no-data-text="Keine andere offene Palette dieses Typs"
          ></v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="moveDialog.open = false">Abbrechen</v-btn>
          <v-btn color="primary" variant="flat" :loading="moveDialog.loading" :disabled="!moveDialog.ziel" @click="confirmMove">Verschieben</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ===================== EINGANGSDATUM (RETURN_DATE) ÄNDERN ===================== -->
    <!-- ============================ POSITION BEARBEITEN ============================ -->
    <v-dialog v-model="editPosDialog.open" max-width="520" persistent>
      <v-card v-if="editPosDialog.item">
        <v-card-title class="d-flex align-center">
          <v-icon start color="orange">mdi-pencil</v-icon>
          Position bearbeiten
        </v-card-title>
        <v-card-text class="pa-4">
          <div class="text-caption text-medium-emphasis mb-3">
            <strong>{{ editPosDialog.item.weborder_id }}</strong>
            <span v-if="editPosDialog.item.sku"> · {{ editPosDialog.item.sku }}</span>
            <span v-if="editPosDialog.item.ware_klasse"> · {{ editPosDialog.item.ware_klasse }}-Ware</span>
          </div>
          <v-row dense>
            <v-col cols="12">
              <v-text-field
                v-model="editPosDialog.tracking"
                label="Trackingnummer"
                density="compact"
                variant="outlined"
                hide-details
                prepend-inner-icon="mdi-barcode"
                clearable
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-select
                v-model="editPosDialog.reason"
                :items="activeReasonItems"
                item-title="label"
                item-value="code"
                label="Rücksendegrund"
                density="compact"
                variant="outlined"
                hide-details
                clearable
              ></v-select>
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="editPosDialog.cmmf"
                label="CMMF (ohne führende Nullen)"
                density="compact"
                variant="outlined"
                hide-details
                prepend-inner-icon="mdi-identifier"
                clearable
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="editPosDialog.open = false">Abbrechen</v-btn>
          <v-btn color="orange" variant="flat" :loading="editPosDialog.loading" @click="saveEditPos">
            <v-icon start>mdi-content-save</v-icon>Speichern
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dateDialog.open" max-width="460">
      <v-card v-if="dateDialog.item">
        <v-card-title class="d-flex align-center">
          <v-icon start color="warning">mdi-calendar-edit</v-icon>
          Eingangsdatum ändern
        </v-card-title>
        <v-card-text>
          <div class="text-caption text-medium-emphasis mb-3">
            {{ dateDialog.item.weborder_id }}<span v-if="dateDialog.item.sku"> · {{ dateDialog.item.sku }}</span>
          </div>
          <v-alert type="info" variant="tonal" density="compact" class="mb-4">
            Das Datum bestimmt das <strong>RETURN_DATE</strong> in der Output-CSV.
            Die Position bleibt in der CSV des Erfassungstags.
          </v-alert>
          <v-text-field
            v-model="dateDialog.datum"
            label="Eingangsdatum (RETURN_DATE)"
            type="date"
            variant="outlined"
            density="compact"
            hide-details
            prepend-inner-icon="mdi-calendar"
          ></v-text-field>
          <div class="text-caption text-medium-emphasis mt-2">
            Leer lassen = Erfassungstag verwenden.
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn variant="text" color="warning" @click="dateDialog.datum = ''">Zurücksetzen</v-btn>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="dateDialog.open = false">Abbrechen</v-btn>
          <v-btn color="warning" variant="flat" :loading="dateDialog.loading" @click="saveDateDialog">Speichern</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useToast } from 'vue-toastification';
import axios from 'axios';
import { API_BASE_URL } from '@/config';

const authStore = useAuthStore();
const toast = useToast();
const API = `${API_BASE_URL}dtc/index.php`;

const isAdmin = computed(() => !!(authStore.user?.admin || authStore.user?.super_admin || authStore.user?.developer));
const hasDtcRole = computed(() => !!authStore.user?.dtc || isAdmin.value);
const hasOfficeRole = computed(() => !!authStore.user?.office || isAdmin.value);
const hasVersandRole = computed(() => !!authStore.user?.lagerist || isAdmin.value);

const tab = ref('erfassung');
const palettenTab = ref('aktiv');
const irmafTab = ref('aktiv');

const statistik = reactive({});
const offenePaletten = ref([]);
const paletten = ref([]);
const irmafListe = ref([]);
const outputLog = ref([]);
const salesStatus = reactive({});

const activePalette = ref(null);

// ----- Rücksendegründe -----
/** Temporär nur T01 erlauben (bis auf Widerruf). */
const DTC_REASON_T01_ONLY = true;

// "match": Stichwörter/Regex zur automatischen Erkennung aus return_reason_raw (englisch).
const reasonItems = [
  { code: 'T01', label: 'T01 – Kunde hat es sich anders überlegt', match: [/changed?\s+(his|her|their|my)?\s*mind/i, /change of mind/i] },
  { code: 'T02', label: 'T02 – Kunde hat nicht das korrekte Produkt erhalten', match: [/did\s?n.?t\s+receive\s+the\s+good\s+product/i, /(wrong|incorrect|not.*correct).*(product|item|article)/i, /did not receive.*correct/i] },
  { code: 'T03', label: 'T03 – Produkt entspricht nicht den Erwartungen', match: [/product\s+does\s+not\s+meet\s+expectation/i, /(not|does not|doesn.?t).*(meet|match).*(expectation)/i, /not as (described|expected)/i, /expectation/i] },
  { code: 'T04', label: 'T04 – Produkt funktioniert nicht', match: [/(not|does not|doesn.?t)\s+work/i, /faulty/i, /defect/i, /broken/i, /malfunction/i] },
  { code: 'T05', label: 'T05 – Paket ist beschädigt angekommen', match: [/parcel\s+arrived\s+damaged/i, /arrived.*damaged/i, /damaged?/i] },
  { code: 'T06', label: 'T06 – Paket ist zu spät angekommen', match: [/parcel\s+arrived\s+(too\s+)?late/i, /arrived.*late/i, /(too )?late/i, /delayed?/i] },
  { code: 'T89', label: 'T89 – nicht zugestellt / Other Reason', match: [/not delivered/i, /undelivered/i, /other reason/i] },
  { code: 'T95', label: 'T95 – nicht zugestellt / Other Reason', match: [/not delivered/i, /undelivered/i, /other reason/i] },
];

const activeReasonItems = computed(() =>
  DTC_REASON_T01_ONLY ? reasonItems.filter((r) => r.code === 'T01') : reasonItems
);

/** Versucht, aus dem englischen Rohtext (return_reason_raw) den T-Code zu erkennen. */
function detectReasonCode(raw) {
  if (DTC_REASON_T01_ONLY) {
    return 'T01';
  }
  const text = (raw || '').trim();
  if (!text) return null;
  // direkter T-Code im Text?
  const codeMatch = text.match(/\bT\d{2}\b/i);
  if (codeMatch) {
    const code = codeMatch[0].toUpperCase();
    if (reasonItems.some(r => r.code === code)) return code;
  }
  // Stichwort-Erkennung
  for (const item of reasonItems) {
    if ((item.match || []).some(rx => rx.test(text))) return item.code;
  }
  return null;
}

// ----- Entscheidungsbäume -----
// node: { question, ja: <key|{result}|{manual}>, nein: <...> }
const Q_VERPACKUNG = 'verpackung';
const Q_UNBENUTZT = 'unbenutzt';
const Q_VOLLSTAENDIG = 'vollstaendig';
const Q_OVP = 'ovp';

// Aufgeteilte Einzelfragen.
// Reihenfolge: In Originalverpackung? -> Produkt vollständig? -> Stark verschmutzt?
// withOvp = false lässt die Originalverpackungs-Frage weg.
function standardTree(withOvp) {
  const firstQ = withOvp ? Q_OVP : Q_VOLLSTAENDIG;
  const tree = {
    geoeffnet: { question: 'Paket geöffnet?', ja: firstQ, nein: Q_VERPACKUNG },
    [Q_VERPACKUNG]: { question: 'Verpackung okay?', ja: { result: 'A' }, nein: firstQ },
    [Q_VOLLSTAENDIG]: { question: 'Produkt vollständig?', ja: Q_UNBENUTZT, nein: { result: 'IRMAF' } },
    // Q_UNBENUTZT-Key bleibt intern; Frage lautet jetzt "Stark verschmutzt?"
    [Q_UNBENUTZT]: { question: 'Stark verschmutzt?', ja: { result: 'IRMAF' }, nein: { result: 'B' } },
    _start: 'geoeffnet',
  };
  if (withOvp) {
    tree[Q_OVP] = { question: 'In Originalverpackung?', ja: Q_VOLLSTAENDIG, nein: { result: 'B' } };
  }
  return tree;
}

const treeStandard = standardTree(true);

const decisionTrees = {
  T01: treeStandard,
  T02: treeStandard,
  T03: treeStandard,
  // T06: "zu spät angekommen" – voller Standardbaum wie T01-T03
  T06: treeStandard,
  // T04: "Produkt funktioniert nicht" – Kunde hat das Paket bereits geöffnet/getestet,
  // daher keine "Paket geöffnet?"-Frage
  T04: {
    [Q_OVP]: { question: 'In Originalverpackung?', ja: Q_VOLLSTAENDIG, nein: { result: 'B' } },
    [Q_VOLLSTAENDIG]: { question: 'Produkt vollständig?', ja: Q_UNBENUTZT, nein: { result: 'IRMAF' } },
    [Q_UNBENUTZT]: { question: 'Stark verschmutzt?', ja: { result: 'IRMAF' }, nein: { result: 'B' } },
    _start: Q_OVP,
  },
  // T05: "Paket beschädigt" – startet mit "Verpackung okay?", dann "Paket geöffnet?"
  T05: {
    verpackung05: { question: 'Verpackung okay?', ja: 'geoeffnet05ok', nein: 'geoeffnet05dmg' },
    // Verpackung okay -> nicht geöffnet = A, geöffnet -> Produktprüfung
    geoeffnet05ok: { question: 'Paket geöffnet?', ja: Q_OVP, nein: { result: 'A' } },
    // Verpackung beschädigt -> nicht geöffnet = B, geöffnet -> Produktprüfung
    geoeffnet05dmg: { question: 'Paket geöffnet?', ja: Q_OVP, nein: { result: 'B' } },
    [Q_OVP]: { question: 'In Originalverpackung?', ja: Q_VOLLSTAENDIG, nein: { result: 'B' } },
    [Q_VOLLSTAENDIG]: { question: 'Produkt vollständig?', ja: Q_UNBENUTZT, nein: { result: 'IRMAF' } },
    [Q_UNBENUTZT]: { question: 'Stark verschmutzt?', ja: { result: 'IRMAF' }, nein: { result: 'B' } },
    _start: 'verpackung05',
  },
  // T89 / T95: keine Prüfung -> manuelle Klassifizierung
  T89: { _manual: true },
  T95: { _manual: true },
};

// ----- Formular-State -----
const form = reactive({
  weborderRaw: '',
  weborderClean: '',
});
const salesRows = ref([]);
// Pro SalesHistory-Zeile: Auswahl-/Erfassungs-State (parallel zu salesRows)
const rowState = ref([]);
const searched = ref(false);

/** Erkennt Frachtkosten-Zeilen (SKU = FREIGHT) – diese sind kein Paket. */
function isFreightRow(r) {
  return /freight/i.test(String(r?.sku ?? '').trim());
}

/** Entfernt fuehrende Nullen aus einer CMMF (z.B. 0000001234 -> 1234). */
function stripCmmf(cmmf) {
  return String(cmmf ?? '').replace(/^0+/, '');
}

// Ausgewählte, buchbare Pakete (keine Fracht)
const selectedItems = computed(() =>
  rowState.value
    .map((st, idx) => ({ ...st, idx, row: salesRows.value[idx] }))
    .filter(s => s.checked && !s.freight && s.row)
);

const irmafHinweis = computed(() => {
  const hit = selectedItems.value.find((s) => s.row && isIrmafDateRow(s.row));
  return hit ? (hit.row.belgien_grund || dateRuleGrund(hit.row) || 'Datumsregel') : '';
});

const rueckKundeHinweis = computed(() => {
  const hit = selectedItems.value.find((s) => s.row && isRueckKundeRow(s.row));
  return hit ? (hit.row.belgien_grund || dateRuleGrund(hit.row) || 'Rückversand Kunde (>100 Tage)') : '';
});

/** Parst order_date aus SalesHistory (YYYY-MM-DD o.ä.). */
function parseOrderDate(r) {
  const s = String(r?.order_date || '').trim();
  if (!s) return null;
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  const de = s.match(/^(\d{2})\.(\d{2})\.(\d{4})/);
  if (de) return new Date(Number(de[3]), Number(de[2]) - 1, Number(de[1]));
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

function startOfToday() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function date100DaysAgo() {
  const d = startOfToday();
  d.setDate(d.getDate() - 100);
  return d;
}

/** Stichtag Belgien/IRMAF (exklusiv: vor diesem Tag). */
const IRMAF_STICHTAG = new Date(2026, 4, 19);

function dateRuleGrund(r) {
  const od = parseOrderDate(r);
  if (!od) return r?.belgien_grund || '';
  if (od < IRMAF_STICHTAG) return 'Bestellt vor 19.05.2026';
  if (od < date100DaysAgo()) return 'Rueckversand Kunde (>100 Tage)';
  return '';
}

/**
 * >100 Tage / Rueckversand-Kunde – live aus order_date (nicht nur Import-Flag).
 */
function isRueckKundeRow(r) {
  if (!r) return false;
  const od = parseOrderDate(r);
  if (od) {
    if (od < IRMAF_STICHTAG) return false;
    return od < date100DaysAgo();
  }
  if (Number(r.belgien_flag) !== 1) return false;
  const g = String(r.belgien_grund || '');
  return /Rueckversand Kunde|>100\s*Tage|Aelter als 100 Tage|Älter als 100 Tage/i.test(g);
}

/** Belgien-Stichtag vor 19.05.2026 → IRMAF (live aus order_date). */
function isIrmafDateRow(r) {
  if (!r) return false;
  const od = parseOrderDate(r);
  if (od) return od < IRMAF_STICHTAG;
  if (Number(r.belgien_flag) !== 1) return false;
  return !isRueckKundeRow(r);
}

// ----- IRMAF-ID Dialog (Promise-basiert) -----
const irmafDialog = reactive({ open: false, rows: [] });
let irmafResolve = null;

/** Fragt pro IRMAF-Position eine IRMAF-ID ab. Resolved Map {key: id} oder null bei Abbruch. */
function askIrmafIds(rows) {
  return new Promise((resolve) => {
    irmafDialog.rows = rows.map(r => ({ ...r, irmaf_id: '' }));
    irmafDialog.open = true;
    irmafResolve = resolve;
  });
}
function confirmIrmafDialog() {
  if (irmafDialog.rows.some(r => !String(r.irmaf_id).trim())) {
    toast.error('Bitte für jeden Eintrag die IRMAF-ID angeben');
    return;
  }
  const map = {};
  irmafDialog.rows.forEach(r => { map[r.key] = String(r.irmaf_id).trim(); });
  irmafDialog.open = false;
  if (irmafResolve) { irmafResolve(map); irmafResolve = null; }
}
function cancelIrmafDialog() {
  irmafDialog.open = false;
  if (irmafResolve) { irmafResolve(null); irmafResolve = null; }
}

// ----- Sepas Dialog (Rueckversand Kunde) -----
const sepasDialog = reactive({ open: false, rows: [] });
let sepasResolve = null;

function askSepasNumbers(rows) {
  return new Promise((resolve) => {
    sepasDialog.rows = rows.map((r) => ({ ...r, sepas: '' }));
    sepasDialog.open = true;
    sepasResolve = resolve;
  });
}
function confirmSepasDialog() {
  if (sepasDialog.rows.some((r) => !String(r.sepas).trim())) {
    toast.error('Bitte für jeden Eintrag die Sepas-Auftragsnummer angeben');
    return;
  }
  const map = {};
  sepasDialog.rows.forEach((r) => { map[r.key] = String(r.sepas).trim(); });
  sepasDialog.open = false;
  if (sepasResolve) { sepasResolve(map); sepasResolve = null; }
}
function cancelSepasDialog() {
  sepasDialog.open = false;
  if (sepasResolve) { sepasResolve(null); sepasResolve = null; }
}

// ----- 1-von-N Bestätigung vor Erfassen -----
const singleSelectConfirm = reactive({ open: false, total: 0 });
let singleSelectResolve = null;

function confirmSingleOfMany(totalBuchbar) {
  return new Promise((resolve) => {
    singleSelectConfirm.total = totalBuchbar;
    singleSelectConfirm.open = true;
    singleSelectResolve = resolve;
  });
}
function resolveSingleSelectConfirm(ok) {
  singleSelectConfirm.open = false;
  if (singleSelectResolve) {
    singleSelectResolve(ok);
    singleSelectResolve = null;
  }
}

// ----- IRMAF-Abschluss Dialog -----
const closeDialog = reactive({ open: false, item: null, typ: 'vereinnahmung', loading: false });
// Wizard-State für die Vereinnahmung (eigene A/B-Ermittlung wie in der Mehrfachauswahl)
const closeWiz = reactive({ tracking: '', reason: null, reasonAuto: false, treeKey: null, manualChoice: false, result: null, palette: 0, mitOutput: false });
const closeWizNode = computed(() => nodeFor(closeWiz));
// Ziel-Paletten passend zur ermittelten Klasse (A/B)
const closeWizPalettenOptions = computed(() => {
  if (closeWiz.result !== 'A' && closeWiz.result !== 'B') return [];
  const list = closeWiz.result === 'A' ? offenePalettenA.value : offenePalettenB.value;
  return [
    { title: 'Automatisch (neueste)', value: 0 },
    ...list.map(p => ({ title: `${p.paletten_nr} (${p.anzahl_positionen} Pos.)`, value: p.id })),
  ];
});

function abschlussLabel(typ) {
  if (typ === 'vereinnahmung') return 'Vereinnahmung';
  if (typ === 'schrott') return 'Schrott';
  if (typ === 'rueckversand') return 'Rückversand';
  if (typ === 'rueckversand_kunde') return 'Rückversand Kunde';
  return typ || '';
}
function abschlussColor(typ) {
  if (typ === 'vereinnahmung') return 'teal';
  if (typ === 'schrott') return 'red';
  if (typ === 'rueckversand') return 'blue';
  if (typ === 'rueckversand_kunde') return 'purple';
  return 'grey';
}
function openCloseDialog(item) {
  closeDialog.item = item;
  closeDialog.typ = 'vereinnahmung';
  closeDialog.open = true;
  // Wizard mit den Falldaten vorbelegen und Entscheidungsbaum starten
  closeWiz.tracking = item.tracking_number || '';
  let reason = item.return_reason_code || detectReasonCode(item.notiz) || 'T01';
  // Temporär nur T01: Legacy-Gründe erzwingen, sonst Speichern blockiert
  if (DTC_REASON_T01_ONLY) {
    reason = 'T01';
  }
  closeWiz.reason = reason;
  closeWiz.reasonAuto = !item.return_reason_code || DTC_REASON_T01_ONLY;
  closeWiz.palette = 0;
  closeWiz.mitOutput = false; // Standard: NICHT in Output (kein_output = 1)
  startTreeFor(closeWiz, reason);
}
function closeWizOnReason() {
  closeWiz.reasonAuto = false;
  startTreeFor(closeWiz, closeWiz.reason);
}
function closeWizReclassify() {
  startTreeFor(closeWiz, closeWiz.reason);
}
function closeWizManual() {
  closeWiz.result = null;
  closeWiz.treeKey = null;
  closeWiz.manualChoice = true;
}
async function confirmCloseIrmaf() {
  if (!closeDialog.item) return;
  const payload = { action: 'close_irmaf', id: closeDialog.item.id, abschluss_typ: closeDialog.typ };
  if (closeDialog.typ === 'vereinnahmung') {
    if (!['A', 'B'].includes(closeWiz.result)) {
      toast.error('Bitte die Klassifizierung abschließen (A oder B)');
      return;
    }
    if (!(closeWiz.tracking || '').trim()) {
      toast.error('Trackingnummer fehlt');
      return;
    }
    payload.ware_klasse = closeWiz.result;
    payload.tracking_number = (closeWiz.tracking || '').trim();
    payload.return_reason_code = closeWiz.reason;
    payload.mit_output = closeWiz.mitOutput ? 1 : 0;
    if (closeWiz.palette) payload.palette_id = closeWiz.palette;
  }
  closeDialog.loading = true;
  try {
    const d = await apiPost(payload);
    if (d.status === 'success') {
      const ziel = closeDialog.typ === 'vereinnahmung' && d.palette_nr ? ` (Palette ${d.palette_nr})` : '';
      toast.success(`Fall abgeschlossen: ${abschlussLabel(closeDialog.typ)}${ziel}`);
      closeDialog.open = false;
      await Promise.all([loadIrmaf(), loadStatistik(), loadOffenePaletten(), refreshActivePalette()]);
    } else {
      toast.error(d.message || 'Fehler');
    }
  } catch (e) {
    toast.error(e?.response?.data?.message || 'Abschluss fehlgeschlagen');
  } finally {
    closeDialog.loading = false;
  }
}
async function reopenIrmaf(item) {
  try {
    const d = await apiPost({ action: 'reopen_irmaf', id: item.id });
    if (d.status === 'success') {
      toast.success('Fall wieder geöffnet');
      await Promise.all([loadIrmaf(), loadStatistik(), loadOffenePaletten(), refreshActivePalette()]);
    } else toast.error(d.message || 'Fehler');
  } catch (e) {
    toast.error(e?.response?.data?.message || 'Fehler');
  }
}

// Aktueller Entscheidungsbaum-Knoten einer Zeile
function rowNode(idx) {
  return nodeFor(rowState.value[idx]);
}

// ----- Generische Entscheidungsbaum-Helfer (Zeile ODER Wizard-State) -----
function nodeFor(st) {
  if (!st || !st.reason || !st.treeKey) return null;
  const tree = decisionTrees[st.reason];
  if (!tree || tree._manual) return null;
  return tree[st.treeKey] || null;
}
function startTreeFor(st, reasonCode) {
  if (!st) return;
  st.result = null;
  st.manualChoice = false;
  st.treeKey = null;
  const tree = decisionTrees[reasonCode];
  if (!tree) return;
  if (tree._manual) { st.manualChoice = true; return; }
  st.treeKey = tree._start;
}
function answerFor(st, yes) {
  const node = nodeFor(st);
  if (!st || !node) return;
  const next = yes ? node.ja : node.nein;
  if (typeof next === 'string') {
    st.treeKey = next;
  } else if (next && next.result) {
    st.result = next.result;
    st.treeKey = null;
    st.manualChoice = false;
  } else if (next && next.manual) {
    st.treeKey = null;
    st.manualChoice = true;
  }
}
function setResultFor(st, r) {
  if (!st) return;
  st.result = r;
  st.manualChoice = false;
}

function resultLabelOf(r) {
  if (r === 'A') return 'A-Ware';
  if (r === 'B') return 'B-Ware';
  if (r === 'IRMAF') return 'IRMAF-Liste';
  return '';
}
function resultColorOf(r) {
  if (r === 'A') return 'teal';
  if (r === 'B') return 'blue';
  if (r === 'IRMAF') return 'orange';
  return 'grey';
}

// Zusammenfassung der Klassifizierungen über alle ausgewählten Pakete
const resultSummary = computed(() => {
  const counts = {};
  for (const s of selectedItems.value) {
    if (s.result) counts[s.result] = (counts[s.result] || 0) + 1;
  }
  return Object.keys(counts).map(result => ({ result, count: counts[result] }));
});

const canSubmit = computed(() => {
  if (!form.weborderClean) return false;
  const items = selectedItems.value;
  if (items.length === 0) return false;
  // jedes ausgewählte Paket braucht CMMF, Trackingnummer, Menge >= 1, Grund und Klassifizierung
  return items.every(s =>
    s.cmmf && (s.tracking || '').trim() !== '' && s.menge >= 1 && s.reason && s.result
  );
});

// ----- Ziel-Palette-Auswahl (bei mehreren offenen Paletten eines Typs) -----
const zielPaletteA = ref(0); // 0 = automatisch
const zielPaletteB = ref(0);
const offenePalettenA = computed(() => offenePaletten.value.filter(p => p.paletten_typ === 'A'));
const offenePalettenB = computed(() => offenePaletten.value.filter(p => p.paletten_typ === 'B'));
const hatA = computed(() => selectedItems.value.some(s => s.result === 'A'));
const hatB = computed(() => selectedItems.value.some(s => s.result === 'B'));
const needPalettenWahl = computed(() =>
  (hatA.value && offenePalettenA.value.length > 1) || (hatB.value && offenePalettenB.value.length > 1)
);
function palettenOptions(typ) {
  const list = typ === 'A' ? offenePalettenA.value : offenePalettenB.value;
  return [
    { title: 'Automatisch (neueste)', value: 0 },
    ...list.map(p => ({ title: `${p.paletten_nr} (${p.anzahl_positionen} Pos.)`, value: p.id })),
  ];
}

// ----- Tabellen-Header -----
const posHeaders = [
  { title: 'Weborder', key: 'weborder_id' },
  { title: 'SKU', key: 'sku' },
  { title: 'CMMF', key: 'cmmf' },
  { title: 'Tracking', key: 'tracking_number' },
  { title: 'Grund', key: 'return_reason_code' },
  { title: 'Menge', key: 'menge' },
  { title: 'Klasse', key: 'ware_klasse' },
  { title: 'Eingang', key: 'return_date', sortable: false },
  { title: '', key: 'actions', sortable: false },
];
const palettenHeaders = [
  { title: 'Nr.', key: 'paletten_nr' },
  { title: 'Typ', key: 'paletten_typ' },
  { title: 'Status', key: 'status' },
  { title: 'Positionen', key: 'anzahl_positionen' },
  { title: 'Gewicht', key: 'gesamtgewicht' },
  { title: 'Erstellt', key: 'erfassung_datum' },
  { title: 'Schein', key: 'actions', sortable: false, align: 'end' },
];
const irmafHeaders = [
  { title: 'Datum', key: 'eingangsdatum' },
  { title: 'IRMAF-ID', key: 'irmaf_id' },
  { title: 'Weborder', key: 'weborder_id' },
  { title: 'SKU', key: 'sku' },
  { title: 'CMMF', key: 'cmmf' },
  { title: 'Grund', key: 'notiz', sortable: false },
  { title: 'Status', key: 'status', sortable: false },
  { title: '', key: 'actions', sortable: false },
];
const outputHeaders = [
  { title: 'Datei', key: 'dateiname' },
  { title: 'Zeilen', key: 'anzahl_zeilen' },
  { title: 'Erstellt', key: 'erstelldatum' },
  { title: 'Von', key: 'generiert_von_name' },
  { title: 'PFS-Upload', key: 'pfs_status', sortable: false },
  { title: '', key: 'actions', sortable: false },
];
const tagHeaders = [
  { title: 'Zeit', key: 'erfasst_datum', sortable: true },
  { title: 'Quelle', key: 'quelle', sortable: true },
  { title: 'Weborder', key: 'weborder_id', sortable: true },
  { title: 'PFS', key: 'pfs_order_id', sortable: true },
  { title: 'SKU', key: 'sku', sortable: true },
  { title: 'CMMF', key: 'cmmf', sortable: true },
  { title: 'Tracking', key: 'tracking_number', sortable: true },
  { title: 'Grund', key: 'return_reason_code', sortable: true },
  { title: 'Klasse', key: 'ware_klasse', sortable: true },
  { title: 'Palette / IRMAF', key: 'paletten_nr', sortable: false },
  { title: 'Output-CSV', key: 'in_output_csv', sortable: true },
  { title: 'CSV-Datei', key: 'output_csv_datei', sortable: true },
  { title: 'Von', key: 'erfasst_von_name', sortable: true },
];
const korrekturHeaders = [
  { title: 'Weborder', key: 'weborder_id' },
  { title: 'Palette', key: 'paletten_nr' },
  { title: 'Status', key: 'palette_status' },
  { title: 'Tracking', key: 'tracking_number' },
  { title: 'Output', key: 'in_output_csv' },
  { title: 'Korrigierbar', key: 'korrektur_erlaubt', sortable: false },
  { title: '', key: 'actions', sortable: false },
];

const korrekturSearch = ref('');
const korrekturResults = ref([]);
const korrekturLoading = ref(false);
const korrekturSaving = ref(false);
const korrekturCsvLoading = ref(false);
const korrekturOutputLoading = ref(false);
const korrekturPaletten = ref([]);
const korrekturOutputOptions = ref([]);
const korrekturHistory = ref([]);
const korrekturHistoryWeborder = ref('');
const korrekturForm = reactive({
  position_id: null,
  weborder_id: '',
  tracking_number: '',
  return_reason_code: 'T01',
  cmmf: '',
  menge: 1,
  return_date: '',
  bewegungTyp: 'none',
  ziel_palette_id: null,
  output_anpassen: false,
  aus_output_entfernen: false,
  in_output_aufnehmen: false,
  ziel_output_modus: 'heute',
  ziel_output_log_id: null,
  in_output_csv: false,
  output_log_id: null,
  output_csv_datei: '',
  hat_output_snapshot: false,
  ware_klasse: '',
  kommentar: '',
});

const downloadableKorrekturOutputId = computed(() => {
  if (korrekturForm.ziel_output_modus === 'export' && korrekturForm.ziel_output_log_id) {
    return korrekturForm.ziel_output_log_id;
  }
  if (korrekturForm.output_log_id && korrekturForm.hat_output_snapshot) {
    return korrekturForm.output_log_id;
  }
  return null;
});

// ----- Weborder-Sperren (Admin) -----
const sperreHeaders = [
  { title: 'Weborder', key: 'weborder_id' },
  { title: 'Grund', key: 'grund' },
  { title: 'Gesperrt von', key: 'gesperrt_von_name' },
  { title: 'Am', key: 'gesperrt_am' },
  { title: '', key: 'actions', sortable: false },
];
const sperreListe = ref([]);
const sperreLoading = ref(false);
const sperreSaving = ref(false);
const sperreUnlockingId = ref(null);
const sperreForm = reactive({
  weborder_id: '',
  grund: '',
});

// ----- API-Helfer -----
async function apiGet(action, params = {}) {
  const res = await axios.get(API, { params: { action, ...params }, withCredentials: true });
  return res.data;
}
async function apiPost(payload) {
  const res = await axios.post(API, payload, { withCredentials: true, headers: { 'Content-Type': 'application/json' } });
  return res.data;
}

// ----- Laden -----
async function loadStatistik() {
  try { const d = await apiGet('get_statistik'); if (d.status === 'success') Object.assign(statistik, d.statistik); } catch (e) { /* ignore */ }
}
async function loadOffenePaletten() {
  try { const d = await apiGet('get_offene_paletten'); if (d.status === 'success') offenePaletten.value = d.paletten; } catch (e) { /* ignore */ }
}
async function loadPaletten() {
  try { const d = await apiGet('get_paletten', { tab: palettenTab.value }); if (d.status === 'success') paletten.value = d.paletten; } catch (e) { /* ignore */ }
}
async function loadIrmaf() {
  try { const d = await apiGet('get_irmaf_liste', { archiviert: irmafTab.value === 'archiv' ? 1 : 0 }); if (d.status === 'success') irmafListe.value = d.eintraege; } catch (e) { /* ignore */ }
}
async function loadSalesStatus() {
  try { const d = await apiGet('get_sales_status'); if (d.status === 'success') Object.assign(salesStatus, d.data); } catch (e) { /* ignore */ }
}
async function loadOutputLog() {
  try { const d = await apiGet('get_output_log'); if (d.status === 'success') outputLog.value = d.eintraege; } catch (e) { /* ignore */ }
}

// ----- Paletten -----
async function createPalette(typ) {
  try {
    const d = await apiPost({ action: 'create_palette', typ });
    if (d.status === 'success') {
      toast.success(`Palette ${d.paletten_nr} erstellt`);
      await Promise.all([loadOffenePaletten(), loadPaletten()]);
    } else toast.error(d.message || 'Fehler');
  } catch (e) { toast.error('Fehler beim Erstellen'); }
}

async function selectActivePalette(p) {
  try {
    const d = await apiGet('get_palette', { palette_id: p.id });
    if (d.status === 'success') { activePalette.value = d.palette; resetForm(); }
  } catch (e) { toast.error('Palette konnte nicht geladen werden'); }
}

async function refreshActivePalette() {
  if (activePalette.value) {
    const d = await apiGet('get_palette', { palette_id: activePalette.value.id });
    if (d.status === 'success') activePalette.value = d.palette;
  }
}

async function finishPalette(p) {
  const target = p || activePalette.value;
  if (!target) return;
  try {
    const d = await apiPost({ action: 'update_palette_status', palette_id: target.id, status: 'office_freigabe' });
    if (d.status === 'success') {
      toast.success(`Palette ${target.paletten_nr || ''} an Office übergeben`);
      // Direkt nach Abschluss den Palettenschein zum Druck öffnen
      await printPalettenschein(target);
      if (activePalette.value && activePalette.value.id === target.id) activePalette.value = null;
      await Promise.all([loadOffenePaletten(), loadPaletten(), loadStatistik()]);
    } else toast.error(d.message || 'Fehler');
  } catch (e) { toast.error('Übergabe fehlgeschlagen'); }
}

// ----- Palettenschein Druck -----
function fmtDatumPrint(val) {
  if (!val) return '—';
  const d = new Date(val);
  if (isNaN(d.getTime())) return String(val);
  return d.toLocaleDateString('de-DE');
}

async function printPalettenschein(palette) {
  try {
    const d = await apiGet('get_palette', { palette_id: palette.id });
    if (d.status !== 'success') { toast.error('Palette konnte nicht geladen werden'); return; }

    const data = d.palette || {};
    const positionen = data.positionen || [];
    const gesamtMenge = positionen.reduce((s, p) => s + (parseInt(p.menge) || 0), 0);
    const hatIrmaf = positionen.some(p => Number(p.kein_output) === 1);

    const rows = positionen.map(pos => {
      const irmaf = Number(pos.kein_output) === 1;
      return `
      <tr${irmaf ? ' style="background:#FFF3E0;"' : ''}>
        <td style="padding:3px;border:1px solid #000;font-size:9px;">${pos.weborder_id || '—'}</td>
        <td style="padding:3px;border:1px solid #000;font-size:9px;">${pos.sku || '—'}</td>
        <td style="padding:3px;border:1px solid #000;font-size:9px;">${irmaf ? '* ' : ''}${fmtCmmf(pos.cmmf) || '—'}</td>
        <td style="padding:3px;border:1px solid #000;font-size:9px;">${pos.tracking_number || '—'}</td>
        <td style="padding:3px;border:1px solid #000;font-size:9px;">${pos.return_reason_code || '—'}</td>
        <td style="padding:3px;border:1px solid #000;text-align:center;">${pos.menge || 0}x</td>
      </tr>`;
    }).join('');

    const logoUrl = `${window.location.origin}/img/dhs-logo.svg`;
    const html = `
      <div style="border:2px solid #000;padding:20px;margin:20px;">
        <div style="text-align:center;margin-bottom:20px;">
          <img src="${logoUrl}" style="width:150px;height:auto;display:block;margin:0 auto 12px;" alt="DHS GmbH">
          <h1 style="margin:0;font-size:20px;">Palettenschein – ${data.paletten_nr || ''}</h1>
        </div>
        <table style="width:100%;margin-bottom:20px;border-collapse:collapse;border:1px solid #000;">
          <tr><th style="background:#f0f0f0;padding:8px;border:1px solid #000;text-align:left;width:35%;">Paletten-Nr.</th><td style="padding:8px;border:1px solid #000;">${data.paletten_nr || '—'}</td></tr>
          <tr><th style="background:#f0f0f0;padding:8px;border:1px solid #000;text-align:left;">Typ</th><td style="padding:8px;border:1px solid #000;">${data.paletten_typ || '—'}-Ware</td></tr>
          <tr><th style="background:#f0f0f0;padding:8px;border:1px solid #000;text-align:left;">Datum</th><td style="padding:8px;border:1px solid #000;">${fmtDatumPrint(new Date().toISOString().split('T')[0])}</td></tr>
          <tr><th style="background:#f0f0f0;padding:8px;border:1px solid #000;text-align:left;">Anzahl Positionen</th><td style="padding:8px;border:1px solid #000;">${positionen.length}</td></tr>
        </table>
        ${positionen.length > 0 ? `
        <h3 style="margin-top:20px;margin-bottom:10px;">Artikel-Liste</h3>
        <table style="width:100%;margin-bottom:10px;border-collapse:collapse;border:1px solid #000;font-size:10px;">
          <thead><tr>
            <th style="background:#f0f0f0;padding:4px;border:1px solid #000;text-align:left;">Weborder</th>
            <th style="background:#f0f0f0;padding:4px;border:1px solid #000;text-align:left;">SKU</th>
            <th style="background:#f0f0f0;padding:4px;border:1px solid #000;text-align:left;">CMMF</th>
            <th style="background:#f0f0f0;padding:4px;border:1px solid #000;text-align:left;">Tracking</th>
            <th style="background:#f0f0f0;padding:4px;border:1px solid #000;text-align:left;">Grund</th>
            <th style="background:#f0f0f0;padding:4px;border:1px solid #000;text-align:center;">Menge</th>
          </tr></thead>
          <tbody>
            ${rows}
            <tr style="background:#f0f0f0;font-weight:bold;">
              <td colspan="5" style="padding:6px;border:1px solid #000;text-align:right;">Gesamtanzahl Artikel:</td>
              <td style="padding:6px;border:1px solid #000;text-align:center;">${gesamtMenge} Stück</td>
            </tr>
          </tbody>
        </table>
        ${hatIrmaf ? `<p style="font-size:10px;color:#E65100;margin:4px 0 16px;"><strong>*</strong> = aus IRMAF-Liste vereinnahmt</p>` : ''}
        ` : '<p style="color:#777;">Keine Positionen auf dieser Palette.</p>'}
      </div>`;

    const w = window.open('', '_blank', 'width=900,height=650');
    if (!w) { toast.error('Popup wurde blockiert – bitte Popups für diese Seite erlauben.'); return; }
    w.document.write(`
      <html><head><title>Palettenschein – ${data.paletten_nr || ''}</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 0; }
        @media print { body { margin: 0; } }
      </style></head>
      <body>${html}</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); w.close(); }, 300);
  } catch (e) {
    toast.error('Fehler beim Erstellen des Palettenscheins');
  }
}

// ----- Erfassung -----
async function searchWeborder() {
  if (!form.weborderRaw.trim()) return;
  try {
    const d = await apiGet('search_weborder', { weborder: form.weborderRaw });
    if (d.status === 'success') {
      form.weborderClean = d.weborder_clean;

      salesRows.value = d.zeilen || [];
      searched.value = true;
      // Zeilen-State aufbauen (Fracht erkennen, Defaults setzen).
      // Jedes Paket hat seinen eigenen Grund + eigene Klassifizierung.
      rowState.value = salesRows.value.map(r => ({
        checked: false,
        freight: isFreightRow(r),
        menge: 1,
        cmmf: '',
        candidates: [],
        tracking: '',
        reason: null,
        reasonAuto: false,
        treeKey: null,
        manualChoice: false,
        result: null,
      }));

      // CMMF direkt vorauflösen, damit eindeutige Treffer schon vorausgewählt sind
      salesRows.value.forEach((r, idx) => {
        if (!rowState.value[idx].freight) resolveRowCmmf(idx);
      });
    } else toast.error(d.message || 'Fehler');
  } catch (e) {
    toast.error(e?.response?.data?.message || 'Weborder-Suche fehlgeschlagen');
  }
}

// CMMF für eine ausgewählte Zeile über die SKU auflösen
async function resolveRowCmmf(idx) {
  const r = salesRows.value[idx];
  const st = rowState.value[idx];
  if (!r || !st) return;
  if (!r.sku) { st.cmmf = r.cmmf || ''; return; }
  try {
    const d = await apiGet('lookup_cmmf', { sku: r.sku });
    if (d.status === 'success') {
      // CMMF ohne fuehrende Nullen + lesbares Label für das Auswahl-Dropdown
      const seen = new Set();
      st.candidates = (d.candidates || [])
        .map(c => {
          const cmmf = stripCmmf(c.cmmf);
          return { ...c, cmmf, label: c.bezeichnung ? `${cmmf} – ${c.bezeichnung}` : cmmf };
        })
        // Doppelte CMMF (z.B. mit/ohne fuehrende Nullen) zusammenfassen
        .filter(c => {
          if (seen.has(c.cmmf)) return false;
          seen.add(c.cmmf);
          return true;
        });
      if (st.candidates.length === 1) {
        st.cmmf = st.candidates[0].cmmf; // eindeutig -> automatisch
      } else if (st.candidates.length > 1) {
        st.cmmf = st.candidates[0].cmmf; // mehrdeutig -> ersten vorauswählen (änderbar)
      } else {
        st.cmmf = stripCmmf(r.cmmf); // kein Treffer -> Fallback
      }
      return;
    }
  } catch (e) { /* ignore */ }
  st.cmmf = stripCmmf(r.cmmf);
}

// Checkbox an/aus: beim Aktivieren CMMF auflösen + Grund automatisch erkennen
function onRowCheck(idx) {
  const st = rowState.value[idx];
  const r = salesRows.value[idx];
  if (!st || st.freight) return;
  if (st.checked) {
    if (!st.cmmf && st.candidates.length === 0) resolveRowCmmf(idx);
    // Grund automatisch aus der eigenen Zeile erkennen, sonst Default T01
    if (!st.reason && r) {
      const detected = detectReasonCode(r.return_reason_raw);
      if (detected) {
        st.reason = detected;
        st.reasonAuto = true;
        rowStartTree(idx, detected);
      } else {
        st.reason = 'T01';
        st.reasonAuto = true;
        rowStartTree(idx, 'T01');
      }
    }
  }
}

// Grund pro Zeile manuell gewählt -> Auto-Hinweis aus, Baum (neu) starten
function rowOnReason(idx) {
  const st = rowState.value[idx];
  if (!st) return;
  st.reasonAuto = false;
  rowStartTree(idx, st.reason);
}

// Entscheidungsbaum einer Zeile starten/zurücksetzen
function rowStartTree(idx, reasonCode) {
  startTreeFor(rowState.value[idx], reasonCode);
}

// Ja/Nein-Antwort für eine Zeile
function rowAnswer(idx, yes) {
  answerFor(rowState.value[idx], yes);
}

// Klassifizierung einer Zeile manuell setzen
function rowSetResult(idx, r) {
  setResultFor(rowState.value[idx], r);
}

// Zeile erneut bewerten (Baum von vorne)
function rowReclassify(idx) {
  const st = rowState.value[idx];
  if (!st) return;
  rowStartTree(idx, st.reason);
}

// Manuelle Klassifizierung erzwingen (A/B/IRMAF-Buttons anzeigen)
function rowManual(idx) {
  const st = rowState.value[idx];
  if (!st) return;
  st.result = null;
  st.treeKey = null;
  st.manualChoice = true;
}

async function submitRetoure() {
  if (!canSubmit.value) return;
  const items = selectedItems.value;

  // Rueckversand-Kunde-Zeilen nicht über Palette/IRMAF buchen
  if (items.some((s) => isRueckKundeRow(s.row))) {
    toast.warning('Ausgewählte Zeile(n) sind Rückversand Kunde (>100 Tage). Bitte „Rückversand Kunde erfassen“ nutzen.');
    return;
  }

  // 1 von N: Bestätigung vor dem Speichern
  const buchbar = rowState.value.filter((s) => s && !s.freight).length;
  if (buchbar > 1 && items.length === 1) {
    const okConfirm = await confirmSingleOfMany(buchbar);
    if (!okConfirm) return;
  }

  // Vor dem Buchen: für alle IRMAF-Positionen die IRMAF-ID abfragen.
  let irmafIdMap = {};
  const irmafItems = items.filter(s => s.result === 'IRMAF');
  if (irmafItems.length) {
    const map = await askIrmafIds(irmafItems.map(s => ({ key: s.idx, sku: s.row.sku, cmmf: s.cmmf })));
    if (!map) return; // abgebrochen
    irmafIdMap = map;
  }

  let ok = 0;
  let irmaf = 0;
  let lastPal = '';
  for (const s of items) {
    const r = s.row;
    // Ziel-Palette: gewählte (falls mehrere offen) sonst automatisch (0)
    let palId = 0;
    if (s.result === 'A') palId = zielPaletteA.value || 0;
    else if (s.result === 'B') palId = zielPaletteB.value || 0;
    const payload = {
      action: 'erfasse_retoure',
      palette_id: palId, // 0 = automatische Zuordnung/Anlage im Backend
      ware_klasse: s.result, // Klassifizierung pro Paket
      weborder_id: form.weborderClean,
      weborder_raw: form.weborderRaw.trim(),
      tracking_number: (s.tracking || '').trim(),
      return_reason_code: s.reason, // Grund pro Paket
      sku: r.sku,
      cmmf: s.cmmf,
      product_id: r.product_id,
      product_desc: r.product_desc,
      order_line: r.order_line,
      pfs_order_id: r.pfs_order_id,
      menge: s.menge,
      gewicht: r.gewicht,
      sales_history_id: r.id || null,
    };
    if (s.result === 'IRMAF') payload.irmaf_id = irmafIdMap[s.idx];
    try {
      const d = await apiPost(payload);
      if (d.status === 'success') {
        ok++;
        if (d.ziel === 'irmaf') irmaf++;
        else lastPal = d.paletten_nr || lastPal;
      } else toast.error((d.message || 'Fehler') + ` (${r.sku})`);
    } catch (e) {
      const msg = e?.response?.data?.message || 'Fehler';
      toast.error(`${msg} (${r.sku})`);
    }
  }
  if (ok > 0) {
    const teile = [];
    if (ok - irmaf > 0) teile.push(`${ok - irmaf} auf Palette${lastPal ? ' ' + lastPal : ''}`);
    if (irmaf > 0) teile.push(`${irmaf} in IRMAF-Liste`);
    toast.success(`${ok} Position(en) erfasst (${teile.join(', ')})`);
    resetForm();
    await Promise.all([loadOffenePaletten(), refreshActivePalette(), loadStatistik(), loadIrmaf()]);
  }
}

async function addToRueckKunde() {
  const items = selectedItems.value;
  const targets = (items.length ? items : rowState.value
    .map((st, idx) => ({ ...st, idx, row: salesRows.value[idx] }))
    .filter((s) => s.row && !s.freight))
    .filter((s) => isRueckKundeRow(s.row));

  if (targets.length === 0) {
    toast.error('Keine Rückversand-Kunde-Zeile ausgewählt');
    return;
  }

  const sepasMap = await askSepasNumbers(
    targets.map((s) => ({ key: s.idx, sku: s.row.sku, cmmf: s.cmmf || s.row.cmmf }))
  );
  if (!sepasMap) return;

  let ok = 0;
  for (const s of targets) {
    const r = s.row;
    try {
      const d = await apiPost({
        action: 'add_rueck_kunde',
        weborder_id: form.weborderClean,
        pfs_order_id: r.pfs_order_id,
        product_id: r.product_id,
        cmmf: s.cmmf || r.cmmf,
        sku: r.sku,
        order_line: r.order_line,
        order_date: r.order_date,
        return_reason_code: s.reason || 'T01',
        tracking_number: (s.tracking || r.tracking_number || '').trim(),
        sepas_auftragsnummer: sepasMap[s.idx],
      });
      if (d.status === 'success') ok++;
      else toast.error(d.message || 'Fehler');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Fehler');
    }
  }
  if (ok > 0) {
    toast.success(`${ok} Rückversand-Kunde-Eintrag/Einträge gespeichert`);
    await Promise.all([loadIrmaf(), loadStatistik()]);
  }
}

async function addToIrmaf() {
  const items = selectedItems.value;
  const targets = items.length ? items : rowState.value
    .map((st, idx) => ({ ...st, idx, row: salesRows.value[idx] }))
    .filter(s => s.row && !s.freight);
  if (targets.length === 0) { toast.error('Keine Zeile ausgewählt'); return; }

  // IRMAF-ID pro Eintrag abfragen
  const idMap = await askIrmafIds(targets.map(s => ({ key: s.idx, sku: s.row.sku, cmmf: s.cmmf || s.row.cmmf })));
  if (!idMap) return; // abgebrochen

  let ok = 0;
  for (const s of targets) {
    const r = s.row;
    try {
      const d = await apiPost({
        action: 'add_irmaf',
        weborder_id: form.weborderClean,
        pfs_order_id: r.pfs_order_id,
        product_id: r.product_id,
        cmmf: s.cmmf || r.cmmf,
        sku: r.sku,
        order_line: r.order_line,
        order_date: r.order_date,
        return_reason_code: s.reason || '',
        tracking_number: (s.tracking || r.tracking_number || '').trim(),
        grund: r.belgien_grund || 'Datumsregel',
        irmaf_id: idMap[s.idx],
      });
      if (d.status === 'success') ok++;
      else toast.error(d.message || 'Fehler');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Fehler');
    }
  }
  if (ok > 0) {
    toast.success(`${ok} Eintrag/Einträge in IRMAF-Liste`);
    await Promise.all([loadIrmaf(), loadStatistik()]);
  }
}

// ----- Manueller IRMAF-Eintrag (Bestellung nicht in SalesHistory) -----
const manualIrmaf = reactive({
  open: false,
  loading: false,
  weborder_id: '',
  sku: '',
  cmmf: '',
  tracking_number: '',
  irmaf_id: '',
});

// SKU-Autovervollständigung
const skuSuggestions = ref([]);
const skuLoading = ref(false);
let skuSearchTimer = null;

function onSkuSearch(q) {
  const query = (q || '').trim();
  if (skuSearchTimer) clearTimeout(skuSearchTimer);
  if (query.length < 2) { skuSuggestions.value = []; return; }
  skuSearchTimer = setTimeout(async () => {
    skuLoading.value = true;
    try {
      const d = await apiGet('search_master_sku', { q: query });
      if (d.status === 'success') {
        skuSuggestions.value = (d.items || []).map(it => ({
          sku: it.sku,
          cmmf: it.cmmf,
          title: it.bezeichnung ? `${it.sku} – ${it.bezeichnung}` : it.sku,
        }));
      }
    } catch (e) { /* ignore */ }
    finally { skuLoading.value = false; }
  }, 250);
}

function onSkuSelect(sku) {
  const hit = skuSuggestions.value.find(s => s.sku === sku);
  manualIrmaf.cmmf = hit ? (hit.cmmf || '') : '';
}

function openManualIrmaf(weborder = '') {
  manualIrmaf.weborder_id = (weborder || '').trim();
  manualIrmaf.sku = '';
  manualIrmaf.cmmf = '';
  manualIrmaf.tracking_number = '';
  manualIrmaf.irmaf_id = '';
  skuSuggestions.value = [];
  manualIrmaf.open = true;
}

async function submitManualIrmaf() {
  if (!manualIrmaf.weborder_id.trim() || !manualIrmaf.irmaf_id.trim()) {
    toast.error('Weborder-ID und IRMAF-ID sind Pflichtfelder');
    return;
  }
  manualIrmaf.loading = true;
  try {
    const d = await apiPost({
      action: 'add_irmaf',
      weborder_id: manualIrmaf.weborder_id.trim(),
      sku: (manualIrmaf.sku || '').trim(),
      cmmf: (manualIrmaf.cmmf || '').trim(),
      tracking_number: manualIrmaf.tracking_number.trim(),
      grund: 'Manuell erfasst',
      irmaf_id: manualIrmaf.irmaf_id.trim(),
    });
    if (d.status === 'success') {
      toast.success('Manueller IRMAF-Eintrag gespeichert');
      manualIrmaf.open = false;
      if (tab.value === 'erfassung') resetForm();
      await Promise.all([loadIrmaf(), loadStatistik()]);
    } else {
      toast.error(d.message || 'Fehler');
    }
  } catch (e) {
    toast.error(e?.response?.data?.message || 'Speichern fehlgeschlagen');
  } finally {
    manualIrmaf.loading = false;
  }
}

async function removePosition(id) {
  try {
    const d = await apiPost({ action: 'remove_position', position_id: id });
    if (d.status === 'success') { await Promise.all([refreshActivePalette(), loadOffenePaletten(), loadStatistik()]); }
  } catch (e) { toast.error('Löschen fehlgeschlagen'); }
}

// ----- Position bearbeiten (Tracking, Grund, CMMF) -----
const editPosDialog = reactive({ open: false, item: null, tracking: '', reason: '', cmmf: '', loading: false });

function openEditPosDialog(item) {
  editPosDialog.item = item;
  editPosDialog.tracking = item.tracking_number || '';
  let reason = item.return_reason_code || '';
  if (DTC_REASON_T01_ONLY && reason !== '' && reason !== 'T01') {
    reason = 'T01';
  }
  editPosDialog.reason = reason;
  editPosDialog.cmmf = item.cmmf || '';
  editPosDialog.open = true;
}

async function saveEditPos() {
  if (!editPosDialog.item) return;
  editPosDialog.loading = true;
  try {
    const d = await apiPost({
      action: 'update_position',
      position_id: editPosDialog.item.id,
      tracking_number: editPosDialog.tracking || '',
      return_reason_code: editPosDialog.reason || '',
      cmmf: editPosDialog.cmmf || '',
    });
    if (d.status === 'success') {
      toast.success('Position aktualisiert');
      editPosDialog.open = false;
      await refreshActivePalette();
    } else {
      toast.error(d.message || 'Fehler beim Speichern');
    }
  } catch (e) {
    toast.error(e?.response?.data?.message || 'Speichern fehlgeschlagen');
  } finally {
    editPosDialog.loading = false;
  }
}

// ----- Eingangs-/Rückgabedatum (RETURN_DATE) je Position ändern -----
const dateDialog = reactive({ open: false, item: null, datum: '', loading: false });

// Anzeige: individuelles return_date oder (Fallback) der Erfassungstag
function fmtEingang(item) {
  const d = item?.return_date || item?.erfasst_datum;
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt)) return '—';
  return dt.toLocaleDateString('de-DE');
}

function fmtCmmf(cmmf) {
  if (!cmmf) return '—';
  const str = String(cmmf).trim();
  if (!str) return '—';
  // Pad to 10 digits with leading zeros
  return str.padStart(18, '0');
}

function openDateDialog(item) {
  dateDialog.item = item;
  // Vorbelegen mit aktuellem return_date oder Erfassungstag (YYYY-MM-DD)
  const src = item?.return_date || item?.erfasst_datum;
  let iso = '';
  if (src) {
    const dt = new Date(src);
    if (!isNaN(dt)) iso = dt.toISOString().slice(0, 10);
  }
  dateDialog.datum = iso;
  dateDialog.open = true;
}

async function saveDateDialog() {
  if (!dateDialog.item) return;
  dateDialog.loading = true;
  try {
    const d = await apiPost({
      action: 'set_position_return_date',
      position_id: dateDialog.item.id,
      typ: 'position',
      return_date: dateDialog.datum || '',
    });
    if (d.status === 'success') {
      toast.success(dateDialog.datum ? 'Eingangsdatum gesetzt' : 'Eingangsdatum zurückgesetzt');
      dateDialog.open = false;
      await refreshActivePalette();
    } else toast.error(d.message || 'Fehler');
  } catch (e) {
    toast.error(e?.response?.data?.message || 'Speichern fehlgeschlagen');
  } finally {
    dateDialog.loading = false;
  }
}

// ----- Position auf andere Palette verschieben -----
const moveDialog = reactive({ open: false, item: null, ziel: 0, loading: false });
const moveDialogOptions = computed(() => {
  if (!moveDialog.item) return [];
  const typ = moveDialog.item.ware_klasse;
  const list = typ === 'A' ? offenePalettenA.value : typ === 'B' ? offenePalettenB.value : [];
  // Aktuelle Palette ausschließen
  return list
    .filter(p => p.id !== moveDialog.item.palette_id)
    .map(p => ({ title: `${p.paletten_nr} (${p.anzahl_positionen} Pos.)`, value: p.id }));
});
function openMoveDialog(item) {
  moveDialog.item = item;
  moveDialog.ziel = 0;
  moveDialog.open = true;
}
async function confirmMove() {
  if (!moveDialog.item || !moveDialog.ziel) return;
  moveDialog.loading = true;
  try {
    const d = await apiPost({ action: 'move_position', position_id: moveDialog.item.id, ziel_palette_id: moveDialog.ziel });
    if (d.status === 'success') {
      toast.success(`Verschoben nach ${d.ziel_palette_nr || ''}`);
      moveDialog.open = false;
      await Promise.all([refreshActivePalette(), loadOffenePaletten(), loadStatistik()]);
    } else toast.error(d.message || 'Fehler');
  } catch (e) {
    toast.error(e?.response?.data?.message || 'Verschieben fehlgeschlagen');
  } finally {
    moveDialog.loading = false;
  }
}

function resetForm() {
  form.weborderRaw = '';
  form.weborderClean = '';
  salesRows.value = [];
  rowState.value = [];
  searched.value = false;
  zielPaletteA.value = 0;
  zielPaletteB.value = 0;
}

// ----- Import -----
const importing = ref(false);
const importResult = ref('');
const importProgress = ref(0);
const importTextArea = ref(null); // DOM-Ref, kein v-model → kein Freeze
const fehlzeilenDialog = ref(false);
const fehlzeilen = ref([]); // Übersprungene Zeilen mit editierbaren Feldern

// Spaltenindizes (0-basiert, identisch zu PHP)
const COL = { WEBORDER:2, PFS:3, ORDER_TYPE:5, ORDER_LINE:6, PRODUCT_ID:8, SKU:10, DESC_A:12, DESC_B:13, REASON:18, ORDER_DATE:19, WEIGHT:16 };

const SALES_HEADER_PATTERNS = [
  /weborder/i,
  /pfs\s*order/i,
  /order\s*type/i,
  /order\s*line/i,
  /^sku$/i,
  /product\s*id/i,
  /product\s*desc/i,
  /order\s*date/i,
  /return\s*reason/i,
  /message\s*type/i,
  /^weight$/i,
  /^gewicht$/i,
  /sales\s*history/i,
  /^report$/i,
];

function cellMatchesSalesHeader(cell) {
  const v = (cell ?? '').trim();
  if (!v) return false;
  if (/^[A-Z]{1,2}$/.test(v)) return true;
  return SALES_HEADER_PATTERNS.some((p) => p.test(v));
}

function looksLikeSalesId(val) {
  const v = (val ?? '').trim();
  if (v.length < 2) return false;
  if (/^(weborder|pfs|order\s*type|order\s*line|sku|product|message\s*type)/i.test(v)) return false;
  if (/^(weborder|pfs).*(id|nummer|number|nr)/i.test(v)) return false;
  if (/\d/.test(v)) return /^[\w()[\].\-/]+$/i.test(v);
  return /^[A-Z]{2,}[A-Z0-9\-_]+$/i.test(v);
}

function isSalesHistoryHeaderRow(cells) {
  const weborder = (cells[COL.WEBORDER] ?? '').trim();
  const pfs = (cells[COL.PFS] ?? '').trim();

  if (weborder && pfs && looksLikeSalesId(weborder) && looksLikeSalesId(pfs)) {
    return false;
  }

  const keyCols = [COL.WEBORDER, COL.PFS, COL.ORDER_TYPE, COL.ORDER_LINE, COL.SKU, COL.PRODUCT_ID, COL.ORDER_DATE];
  let headerHits = 0;
  for (const col of keyCols) {
    if (cellMatchesSalesHeader(cells[col])) headerHits++;
  }
  if (headerHits >= 1) return true;

  if (!weborder && !pfs) {
    const rowText = cells.slice(0, 20).join(' ').toLowerCase();
    if (/weborder|pfs|order type|sales history|message type/.test(rowText)) return true;
    const nonEmpty = cells.map((c) => (c ?? '').trim()).filter(Boolean);
    if (nonEmpty.length > 0 && !nonEmpty.some((c) => /\d{3,}/.test(c))) {
      if (nonEmpty.some((c) => cellMatchesSalesHeader(c))) return true;
    }
  }

  return false;
}

function parseImportText(raw) {
  const lines = raw.split(/\r?\n/);
  const rows = [];
  let skipped = 0;
  let headerRows = 0;
  const fehlzeilen = []; // Zeilen mit fehlenden Pflichtfeldern
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim()) continue;
    const c = line.split('\t');
    if (isSalesHistoryHeaderRow(c)) {
      headerRows++;
      continue;
    }
    const weborder = (c[COL.WEBORDER] ?? '').trim().toUpperCase();
    const pfs      = (c[COL.PFS]      ?? '').trim();
    if (!weborder || !pfs) {
      skipped++;
      fehlzeilen.push({
        zeile: i + 1,
        weborder_id:  weborder,
        pfs_order_id: pfs,
        sku:          (c[COL.SKU]       ?? '').trim(),
        order_date:   (c[COL.ORDER_DATE] ?? '').trim(),
        raw:          line,
        fehler:       !weborder && !pfs ? 'Weborder + PFS fehlen' : !weborder ? 'Weborder fehlt' : 'PFS fehlt',
      });
      continue;
    }

    const orderLineRaw = (c[COL.ORDER_LINE] ?? '').trim();
    const orderLineNum = orderLineRaw.includes(',')
      ? parseInt(orderLineRaw.split(',')[0], 10)
      : parseInt(orderLineRaw.replace(/\D/g, ''), 10) || 0;

    const descA = (c[COL.DESC_A] ?? '').trim();
    const descB = (c[COL.DESC_B] ?? '').trim();

    rows.push({
      weborder_id:       weborder,
      pfs_order_id:      pfs,
      order_type:        (c[COL.ORDER_TYPE]  ?? '').trim(),
      order_line:        orderLineRaw,
      order_line_num:    orderLineNum,
      order_date:        (c[COL.ORDER_DATE]  ?? '').trim(),
      sku:               (c[COL.SKU]         ?? '').trim(),
      product_id:        (c[COL.PRODUCT_ID]  ?? '').trim(),
      product_desc:      [descA, descB].filter(Boolean).join(' ').replace(/\s+/g, ' '),
      return_reason_raw: (c[COL.REASON]      ?? '').trim(),
      gewicht:           (c[COL.WEIGHT]      ?? '').trim(),
    });
  }
  return { rows, skipped, fehlzeilen, headerRows };
}

function clearImport() {
  if (importTextArea.value) importTextArea.value.value = '';
  importResult.value = '';
  importProgress.value = 0;
  fehlzeilen.value = [];
}

async function importSales() {
  const raw = importTextArea.value?.value ?? '';
  if (!raw.trim()) { toast.error('Kein Inhalt eingefügt'); return; }
  importing.value = true;
  importProgress.value = 5;
  importResult.value = 'Zeilen werden analysiert …';

  const { rows, skipped: skippedParse, fehlzeilen: fz, headerRows } = parseImportText(raw);
  fehlzeilen.value = fz.map(z => ({ ...z, weborder_id_edit: z.weborder_id, pfs_order_id_edit: z.pfs_order_id }));

  if (rows.length === 0) {
    toast.error(headerRows > 0
      ? 'Nur Kopfzeilen erkannt – bitte auch Datensätze einfügen'
      : 'Keine importierbaren Zeilen gefunden (leere Weborder/PFS?)');
    importing.value = false; importProgress.value = 0; return;
  }

  const CHUNK = 10000;
  let importedTotal = 0; let updatedTotal = 0; let irmafTotal = 0; let skippedTotal = skippedParse;
  const chunks = Math.ceil(rows.length / CHUNK);

  try {
    for (let i = 0; i < chunks; i++) {
      const chunk = rows.slice(i * CHUNK, (i + 1) * CHUNK);
      importResult.value = `Chunk ${i + 1}/${chunks} (${chunk.length} Zeilen) …`;
      importProgress.value = Math.round(5 + (i / chunks) * 90);

      const res = await apiPost({ action: 'import_sales_history', rows: chunk });
      if (res.status === 'success') {
        importedTotal += res.imported ?? 0;
        updatedTotal  += res.updated ?? 0;
        irmafTotal    += res.irmaf_pflichtig ?? 0;
        skippedTotal  += res.skipped  ?? 0;
      } else {
        toast.error(`Fehler in Chunk ${i + 1}: ${res.message}`);
        importing.value = false; return;
      }
    }

    importProgress.value = 100;
    const headerInfo = headerRows > 0 ? `, Kopfzeilen erkannt: ${headerRows}` : '';
    importResult.value = `Neu: ${importedTotal}, aktualisiert: ${updatedTotal}, IRMAF-pflichtig: ${irmafTotal}, übersprungen: ${skippedTotal}${headerInfo}`;
    if (importTextArea.value) importTextArea.value.value = '';
    toast.success('Import abgeschlossen');
    await Promise.all([loadSalesStatus(), loadStatistik(), loadIrmaf()]);
  } catch (e) {
    toast.error('Import fehlgeschlagen');
    importProgress.value = 0;
  } finally {
    importing.value = false;
  }
}

// ----- Masterliste-Import (SKU · EAN · CMMF · Bezeichnung) -----
const masterText = ref('');
const masterImporting = ref(false);
const masterResult = ref('');
const masterStatus = reactive({});

async function loadMasterStatus() {
  try { const d = await apiGet('get_master_status'); if (d.status === 'success') Object.assign(masterStatus, d.data); } catch (e) { /* ignore */ }
}

async function importMaster() {
  const lines = masterText.value.split(/\r?\n/).filter(l => l.trim() !== '');
  if (lines.length === 0) { toast.error('Keine Daten'); return; }
  const rows = [];
  for (const line of lines) {
    const c = line.split('\t').map(s => (s || '').trim());
    const sku = c[0] || '';
    // Kopfzeile überspringen
    if (sku === '' || /^sku$/i.test(sku)) continue;
    const cmmf = c[2] || '';
    if (!cmmf) continue;
    rows.push({ sku, ean: c[1] || '', cmmf, bezeichnung: c[3] || '' });
  }
  if (rows.length === 0) { toast.error('Keine gültigen Zeilen erkannt'); return; }
  masterImporting.value = true;
  masterResult.value = '';
  try {
    const d = await apiPost({ action: 'import_artikel_master', rows });
    if (d.status === 'success') {
      masterResult.value = `Neu: ${d.imported}, aktualisiert: ${d.updated ?? 0}, übersprungen: ${d.skipped}`;
      toast.success('Masterliste importiert');
      await loadMasterStatus();
    } else toast.error(d.message || 'Fehler');
  } catch (e) { toast.error('Import fehlgeschlagen'); }
  finally { masterImporting.value = false; }
}

// ----- Artikel-Katalog (Lesemodus, im Masterliste-Tab) -----
const dtcKatalogItems = ref([]);
const dtcKatalogSuche = ref('');
const loadingDtcKatalog = ref(false);
const dtcKatalogGefiltert = computed(() => {
  const q = (dtcKatalogSuche.value || '').toLowerCase().trim();
  if (!q) return dtcKatalogItems.value;
  return dtcKatalogItems.value.filter(a =>
    (a.sku || '').toLowerCase().includes(q) ||
    (a.cmmf || '').toLowerCase().includes(q) ||
    (a.ean || '').toLowerCase().includes(q) ||
    (a.bezeichnung || '').toLowerCase().includes(q)
  );
});
async function loadDtcKatalog() {
  loadingDtcKatalog.value = true;
  try {
    const d = await apiGet('get_master_katalog');
    if (d.status === 'success') dtcKatalogItems.value = d.items || [];
    else toast.error(d.message || 'Fehler beim Laden des Katalogs');
  } catch (e) { toast.error('Katalog konnte nicht geladen werden'); }
  finally { loadingDtcKatalog.value = false; }
}

// ----- SalesHistory-Suche -----
const shSuchBegriff = ref('');
const shSuchFeld = ref('all');
const shDateFrom = ref('');
const shDateTo = ref('');
const shErgebnisse = ref([]);
const loadingShSuche = ref(false);

async function sucheSalesHistory() {
  loadingShSuche.value = true;
  try {
    const params = { q: shSuchBegriff.value || '', field: shSuchFeld.value };
    if (shDateFrom.value) params.date_from = shDateFrom.value;
    if (shDateTo.value)   params.date_to   = shDateTo.value;
    const d = await apiGet('search_sales_history', params);
    if (d.status === 'success') shErgebnisse.value = d.rows || [];
    else toast.error(d.message || 'Suche fehlgeschlagen');
  } catch (e) { toast.error('Suche fehlgeschlagen'); }
  finally { loadingShSuche.value = false; }
}

// ----- Tagesübersicht -----
const todayIso = () => new Date().toISOString().slice(0, 10);
const tagDateFrom = ref(todayIso());
const tagDateTo = ref(todayIso());
const tagAlleTage = ref(false);
const tagOutputFilter = ref('all');
const tagQuelleFilter = ref('all');
const tagSuchBegriff = ref('');
const tagErgebnisse = ref([]);
const tagSummary = reactive({});
const loadingTageserfassung = ref(false);

function tagIrmafLabel(item) {
  if (item.quelle !== 'irmaf') return 'Position';
  const status = item.irmaf_status || 'offen';
  const typ = item.abschluss_typ || '';
  if (status === 'offen') return 'IRMAF (offen)';
  if (typ === 'vereinnahmung') return 'IRMAF (Verein.)';
  if (typ === 'schrott') return 'IRMAF (Schrott)';
  if (typ === 'rueckversand') return 'IRMAF (Rücksend.)';
  return 'IRMAF';
}

function tagIrmafColor(item) {
  if (item.quelle !== 'irmaf') return 'teal';
  const status = item.irmaf_status || 'offen';
  const typ = item.abschluss_typ || '';
  if (status === 'offen') return 'orange-darken-2';
  if (typ === 'vereinnahmung' && item.in_output_csv) return 'teal';
  if (typ === 'vereinnahmung') return 'blue';
  return 'grey';
}

function tagIrmafTooltip(item) {
  if (item.quelle !== 'irmaf') return '';
  const status = item.irmaf_status || 'offen';
  const typ = item.abschluss_typ || '';
  if (status === 'offen') return 'Noch nicht bearbeitet – erscheint nicht in der Output-CSV';
  if (typ === 'vereinnahmung' && item.in_output_csv) return 'Vereinnahmung mit Output-CSV-Aufnahme';
  if (typ === 'vereinnahmung') return 'Vereinnahmung ohne Output-CSV-Aufnahme (kein_output)';
  if (typ === 'schrott') return 'Als Schrott abgeschlossen – kein Output';
  if (typ === 'rueckversand') return 'Rückversand – kein Output';
  return '';
}

async function loadTageserfassung() {
  loadingTageserfassung.value = true;
  try {
    const params = {
      output_filter: tagOutputFilter.value,
      source: tagQuelleFilter.value,
      q: tagSuchBegriff.value || '',
    };
    if (tagAlleTage.value) {
      params.all = 1;
    } else {
      params.date_from = tagDateFrom.value || '';
      params.date_to = tagDateTo.value || '';
    }
    const d = await apiGet('get_tageserfassung', params);
    if (d.status === 'success') {
      tagErgebnisse.value = (d.rows || []).map((row) => ({
        ...row,
        paletten_nr: row.quelle === 'irmaf'
          ? (row.paletten_nr ? `Palette ${row.paletten_nr}` : (row.irmaf_id ? `IRMAF ${row.irmaf_id}` : '—'))
          : (row.paletten_nr || '—'),
        in_output_csv: !!Number(row.in_output_csv),
        output_csv_datei: row.output_csv_datei || '',
      }));
      Object.assign(tagSummary, d.summary || {});
    } else {
      toast.error(d.message || 'Laden fehlgeschlagen');
    }
  } catch (e) {
    toast.error('Tagesübersicht konnte nicht geladen werden');
  } finally {
    loadingTageserfassung.value = false;
  }
}

// ----- Fehlzeile einzeln nachimportieren -----
async function importFehlzeile(z, i) {
  z.importing = true;
  z.ergebnis = null;
  try {
    const c = z.raw.split('\t');
    const orderLineRaw = (c[COL.ORDER_LINE] ?? '').trim();
    const descA = (c[COL.DESC_A] ?? '').trim();
    const descB = (c[COL.DESC_B] ?? '').trim();
    const row = {
      weborder_id:       z.weborder_id_edit.trim().toUpperCase(),
      pfs_order_id:      z.pfs_order_id_edit.trim(),
      order_type:        (c[COL.ORDER_TYPE]  ?? '').trim(),
      order_line:        orderLineRaw,
      order_line_num:    orderLineRaw.includes(',')
        ? parseInt(orderLineRaw.split(',')[0], 10)
        : parseInt(orderLineRaw.replace(/\D/g, ''), 10) || 0,
      order_date:        (c[COL.ORDER_DATE]  ?? '').trim(),
      sku:               (c[COL.SKU]         ?? '').trim(),
      product_id:        (c[COL.PRODUCT_ID]  ?? '').trim(),
      product_desc:      [descA, descB].filter(Boolean).join(' ').replace(/\s+/g, ' '),
      return_reason_raw: (c[COL.REASON]      ?? '').trim(),
      gewicht:           (c[COL.WEIGHT]      ?? '').trim(),
    };
    const res = await apiPost({ action: 'import_sales_history', rows: [row] });
    if (res.status === 'success') {
      z.ergebnis = 'ok';
      toast.success(`Zeile ${z.zeile} importiert`);
    } else {
      z.ergebnis = res.message || 'Fehler';
    }
  } catch (e) {
    z.ergebnis = 'Netzwerkfehler';
  } finally {
    z.importing = false;
  }
}

// ----- Output -----
const outputDate = ref(new Date().toISOString().slice(0, 10));
const confirmingId = ref(null);
const downloadingLogId = ref(null);

/** Datum/Uhrzeit lesbar formatieren (DE). */
function formatDateTime(val) {
  if (!val) return '';
  const d = new Date(String(val).replace(' ', 'T'));
  if (isNaN(d.getTime())) return val;
  return d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/** Bestätigt, dass die CSV ins PFS-Portal hochgeladen wurde (Nutzer + Zeit). */
async function confirmPfsUpload(item) {
  confirmingId.value = item.id;
  try {
    const d = await apiPost({ action: 'confirm_pfs_upload', output_id: item.id });
    if (d.status === 'success') {
      toast.success('PFS-Upload bestätigt');
      await loadOutputLog();
    } else {
      toast.error(d.message || 'Fehler');
    }
  } catch (e) {
    toast.error(e?.response?.data?.message || 'Bestätigung fehlgeschlagen');
  } finally {
    confirmingId.value = null;
  }
}

function triggerBlobDownload(res, filenameOverride, fallbackFilename) {
  const blob = new Blob([res.data], { type: 'text/plain' });
  const link = document.createElement('a');
  const objectUrl = URL.createObjectURL(blob);
  link.href = objectUrl;
  const cd = res.headers['content-disposition'] || '';
  const m = cd.match(/filename="?([^";]+)"?/);
  link.download = filenameOverride || (m ? m[1] : fallbackFilename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(objectUrl);
}

async function downloadOutput() {
  try {
    const datePart = outputDate.value.replace(/-/g, '');
    const timePart = new Date().toISOString().replace(/[-:T]/g, '').slice(8, 14);
    const fallback = `DHL_retconf_DACH_${datePart}${timePart}.csv`;
    const res = await axios.get(
      `${API_BASE_URL}dtc/output_csv.php?date=${outputDate.value}&include_irmaf=0`,
      { withCredentials: true, responseType: 'blob' }
    );
    triggerBlobDownload(res, null, fallback);
    await loadOutputLog();
  } catch (e) { toast.error('Download fehlgeschlagen'); }
}

/** Erneuter Download eines bereits protokollierten Exports (gleiches Datum, kein neuer Log-Eintrag). */
async function downloadOutputLog(item) {
  const date = String(item.zeitraum_von ?? '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    toast.error('Export-Datum nicht verfügbar');
    return;
  }
  downloadingLogId.value = item.id;
  try {
    const res = await axios.get(
      `${API_BASE_URL}dtc/output_csv.php?date=${date}&include_irmaf=0&skip_log=1&output_id=${item.id}`,
      { withCredentials: true, responseType: 'blob' }
    );
    const fallback = item.dateiname || `DHL_retconf_DACH_${date.replace(/-/g, '')}.csv`;
    triggerBlobDownload(res, item.dateiname || null, fallback);
  } catch (e) {
    toast.error('Download fehlgeschlagen');
  } finally {
    downloadingLogId.value = null;
  }
}

function resetKorrekturForm() {
  Object.assign(korrekturForm, {
    position_id: null,
    weborder_id: '',
    tracking_number: '',
    return_reason_code: 'T01',
    cmmf: '',
    menge: 1,
    return_date: '',
    bewegungTyp: 'none',
    ziel_palette_id: null,
    output_anpassen: false,
    aus_output_entfernen: false,
    in_output_aufnehmen: false,
    ziel_output_modus: 'heute',
    ziel_output_log_id: null,
    in_output_csv: false,
    output_log_id: null,
    output_csv_datei: '',
    hat_output_snapshot: false,
    ware_klasse: '',
    kommentar: '',
  });
}

async function loadKorrekturOutputOptions() {
  korrekturOutputLoading.value = true;
  try {
    const d = await apiGet('get_output_log');
    if (d.status === 'success') {
      korrekturOutputOptions.value = (d.eintraege || []).map((o) => {
        const datum = String(o.zeitraum_von ?? '').slice(0, 10);
        return {
          id: o.id,
          label: `${o.dateiname || 'Export'} · ${datum || '—'}${o.pfs_bestaetigt_am ? ' · PFS ok' : ''}`,
        };
      });
    }
  } catch (e) {
    korrekturOutputOptions.value = [];
  } finally {
    korrekturOutputLoading.value = false;
  }
}

async function loadKorrekturHistory(weborderId) {
  if (!weborderId) {
    korrekturHistoryWeborder.value = '';
    korrekturHistory.value = [];
    return;
  }
  try {
    const d = await apiGet('admin_get_weborder_history', { weborder_id: weborderId });
    if (d.status === 'success') {
      korrekturHistoryWeborder.value = d.weborder_id;
      korrekturHistory.value = d.historie || [];
    }
  } catch (e) {
    korrekturHistory.value = [];
  }
}

async function loadKorrekturPaletten(wareKlasse) {
  if (!wareKlasse) {
    korrekturPaletten.value = [];
    return;
  }
  try {
    const d = await apiGet('admin_get_korrektur_paletten', { ware_klasse: wareKlasse });
    if (d.status === 'success') {
      korrekturPaletten.value = (d.paletten || []).map((p) => ({
        ...p,
        label: `${p.paletten_nr} (${statusText(p.status)}, ${p.anzahl_positionen} Pos.)`,
      }));
    }
  } catch (e) {
    korrekturPaletten.value = [];
  }
}

async function searchKorrektur() {
  const q = korrekturSearch.value.trim();
  if (!q) {
    toast.warning('Suchbegriff eingeben');
    return;
  }
  korrekturLoading.value = true;
  try {
    const d = await apiGet('admin_search_korrektur', { q });
    if (d.status === 'success') {
      const pos = d.positionen || [];
      const rueck = (d.rueck_kunde || []).map((r) => ({
        ...r,
        id: `rueck-${r.irmaf_id_pk}`,
        paletten_nr: r.sepas_auftragsnummer ? `Sepas ${r.sepas_auftragsnummer}` : 'Rück Kunde',
        palette_status: '—',
        ware_klasse: 'RUECK',
        korrektur_erlaubt: false,
      }));
      korrekturResults.value = [...pos, ...rueck];
      if (pos.length === 1 && rueck.length === 0) {
        selectKorrekturPosition(pos[0]);
      } else {
        resetKorrekturForm();
      }
      if (korrekturResults.value.length === 0) {
        toast.info('Keine Treffer');
      }
    } else {
      toast.error(d.message || 'Suche fehlgeschlagen');
    }
  } catch (e) {
    toast.error('Suche fehlgeschlagen');
  } finally {
    korrekturLoading.value = false;
  }
}

async function selectKorrekturPosition(item) {
  if (!item?.korrektur_erlaubt) {
    toast.warning('Diese Position ist nicht mehr korrigierbar');
    return;
  }
  const rd = item.return_date ? String(item.return_date).slice(0, 10) : '';
  Object.assign(korrekturForm, {
    position_id: item.id,
    weborder_id: item.weborder_id,
    tracking_number: item.tracking_number || '',
    return_reason_code: DTC_REASON_T01_ONLY ? 'T01' : (item.return_reason_code || 'T01'),
    cmmf: item.cmmf || '',
    menge: item.menge || 1,
    return_date: rd,
    bewegungTyp: 'none',
    ziel_palette_id: null,
    output_anpassen: false,
    aus_output_entfernen: false,
    in_output_aufnehmen: false,
    ziel_output_modus: 'heute',
    ziel_output_log_id: null,
    in_output_csv: !!item.in_output_csv,
    output_log_id: item.output_log_id || null,
    output_csv_datei: item.output_csv_datei || '',
    hat_output_snapshot: !!item.hat_output_snapshot,
    ware_klasse: item.ware_klasse || '',
    kommentar: '',
  });
  await Promise.all([
    loadKorrekturPaletten(item.ware_klasse),
    loadKorrekturHistory(item.weborder_id),
    loadKorrekturOutputOptions(),
  ]);
}

async function submitKorrektur() {
  if (!korrekturForm.position_id) return;
  if (!korrekturForm.kommentar.trim()) {
    toast.warning('Kommentar ist Pflicht');
    return;
  }
  if (korrekturForm.bewegungTyp === 'move' && !korrekturForm.ziel_palette_id) {
    toast.warning('Ziel-Palette wählen');
    return;
  }
  if (
    korrekturForm.output_anpassen &&
    korrekturForm.in_output_aufnehmen &&
    korrekturForm.ziel_output_modus === 'export' &&
    !korrekturForm.ziel_output_log_id
  ) {
    toast.warning('Ziel-Export für Output-Einfügen wählen');
    return;
  }
  korrekturSaving.value = true;
  try {
    const payload = {
      action: 'admin_korrektur',
      position_id: korrekturForm.position_id,
      kommentar: korrekturForm.kommentar.trim(),
      tracking_number: korrekturForm.tracking_number,
      return_reason_code: korrekturForm.return_reason_code,
      cmmf: korrekturForm.cmmf,
      menge: korrekturForm.menge,
      return_date: korrekturForm.return_date || '',
      mit_verschiebung: korrekturForm.bewegungTyp === 'move' ? 1 : 0,
      ziel_palette_id: korrekturForm.bewegungTyp === 'move' ? korrekturForm.ziel_palette_id : 0,
      output_anpassen: korrekturForm.output_anpassen ? 1 : 0,
      aus_output_entfernen: korrekturForm.aus_output_entfernen ? 1 : 0,
      in_output_aufnehmen: korrekturForm.in_output_aufnehmen ? 1 : 0,
      ziel_output_modus: korrekturForm.ziel_output_modus,
      ziel_output_log_id: korrekturForm.ziel_output_log_id || 0,
      output_log_id: korrekturForm.output_log_id || 0,
    };
    const d = await apiPost(payload);
    if (d.status === 'success') {
      toast.success('Korrektur gespeichert');
      const savedId = korrekturForm.position_id;
      const savedWeborder = korrekturForm.weborder_id;
      await searchKorrektur();
      const again = korrekturResults.value.find((r) => r.id === savedId);
      if (again) {
        await selectKorrekturPosition(again);
      } else {
        await loadKorrekturHistory(savedWeborder);
      }
      await loadStatistik();
    } else {
      toast.error(d.message || 'Korrektur fehlgeschlagen');
    }
  } catch (e) {
    toast.error(e.response?.data?.message || 'Korrektur fehlgeschlagen');
  } finally {
    korrekturSaving.value = false;
  }
}

async function downloadKorrekturCsv() {
  const outputId = downloadableKorrekturOutputId.value;
  if (!outputId) return;
  korrekturCsvLoading.value = true;
  try {
    const opt = korrekturOutputOptions.value.find((o) => o.id === outputId);
    const res = await axios.get(
      `${API_BASE_URL}dtc/output_csv.php?skip_log=1&output_id=${outputId}&mode=corrected`,
      { withCredentials: true, responseType: 'blob' }
    );
    const name =
      opt?.label?.split(' · ')[0] ||
      korrekturForm.output_csv_datei?.split(',')[0]?.trim() ||
      null;
    triggerBlobDownload(res, name, 'korrigiert.csv');
  } catch (e) {
    toast.error('Download fehlgeschlagen');
  } finally {
    korrekturCsvLoading.value = false;
  }
}

// ----- Reaktionen -----
const statusMap = {
  erfassung: { c: 'orange', t: 'In Erfassung' },
  office_freigabe: { c: 'blue', t: 'Bei Office' },
  versand_final: { c: 'purple', t: 'Beim Versand' },
  abgeschlossen: { c: 'green', t: 'Abgeschlossen' },
};
function statusColor(s) { return statusMap[s]?.c || 'grey'; }
function statusText(s) { return statusMap[s]?.t || s; }

async function loadSperren() {
  if (!isAdmin.value) return;
  sperreLoading.value = true;
  try {
    const d = await apiGet('admin_list_weborder_sperren');
    if (d.status === 'success') sperreListe.value = d.sperren || [];
    else toast.error(d.message || 'Sperren konnten nicht geladen werden');
  } catch (e) {
    toast.error(e?.response?.data?.message || 'Sperren konnten nicht geladen werden');
  } finally {
    sperreLoading.value = false;
  }
}

async function sperreWeborder() {
  const weborder = sperreForm.weborder_id.trim();
  if (!weborder) return;
  sperreSaving.value = true;
  try {
    const d = await apiPost({
      action: 'admin_sperre_weborder',
      weborder_id: weborder,
      grund: sperreForm.grund.trim(),
    });
    if (d.status === 'success') {
      toast.success(`Weborder ${d.weborder_id || weborder} gesperrt`);
      sperreForm.weborder_id = '';
      sperreForm.grund = '';
      await loadSperren();
    } else {
      toast.error(d.message || 'Sperren fehlgeschlagen');
    }
  } catch (e) {
    toast.error(e?.response?.data?.message || 'Sperren fehlgeschlagen');
  } finally {
    sperreSaving.value = false;
  }
}

async function entsperreWeborder(item) {
  if (!item?.id) return;
  sperreUnlockingId.value = item.id;
  try {
    const d = await apiPost({
      action: 'admin_entsperre_weborder',
      id: item.id,
      weborder_id: item.weborder_id,
    });
    if (d.status === 'success') {
      toast.success(`Weborder ${item.weborder_id} entsperrt`);
      await loadSperren();
    } else {
      toast.error(d.message || 'Entsperren fehlgeschlagen');
    }
  } catch (e) {
    toast.error(e?.response?.data?.message || 'Entsperren fehlgeschlagen');
  } finally {
    sperreUnlockingId.value = null;
  }
}

watch(palettenTab, loadPaletten);
watch(irmafTab, loadIrmaf);
watch(tab, (t) => {
  if (t === 'paletten') loadPaletten();
  else if (t === 'irmaf') loadIrmaf();
  else if (t === 'import') loadSalesStatus();
  else if (t === 'master') loadMasterStatus();
  else if (t === 'tagesuebersicht') loadTageserfassung();
  else if (t === 'output') loadOutputLog();
  else if (t === 'korrektur' && isAdmin.value) {
    resetKorrekturForm();
    korrekturHistoryWeborder.value = '';
    korrekturHistory.value = [];
  } else if (t === 'sperren' && isAdmin.value) {
    loadSperren();
  }
});

onMounted(async () => {
  await Promise.all([loadStatistik(), loadOffenePaletten()]);
});
</script>

<style scoped>
.dtc-container { max-width: 1400px; }
.step-box {
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
}
.step-title { font-weight: 600; margin-bottom: 6px; }
.decision-box { background: rgba(0, 150, 136, 0.06); }
.decision-question { font-size: 1.05rem; }
.result-box { background: rgba(255, 255, 255, 0.04); }
.sales-row { padding: 2px 0; }
.freight-row { opacity: 0.6; }
.irmaf-pos { color: #ffb74d; font-weight: 600; }
.package-detail {
  background: rgba(0, 150, 136, 0.05);
  border: 1px solid rgba(0, 150, 136, 0.25);
  border-radius: 8px;
  padding: 10px 12px;
  margin: 4px 0 10px 28px;
}
.korrektur-timeline :deep(.v-timeline-item__body) {
  min-width: 0;
  word-break: normal;
  overflow-wrap: anywhere;
}
</style>
